import { useState, useEffect } from 'react';
import { useContentManager } from '../../../hooks/useContentManager';
import type { ContentItem, SliderItem } from '../../../hooks/useContentManager';
import MediaUploader from '../../../components/admin/MediaUploader';
import BulkImageUploader from '../../../components/admin/BulkImageUploader';
import { api } from '../../../lib/api';
import BrandContentManager from './BrandContentManager';

export default function ContentManager() {
  const { content, sliders, loading, updateContentItem, deleteContentItem, updateSliderItem, deleteSliderItem, reloadContent, getContentItem } = useContentManager();
  const [activeTab, setActiveTab] = useState('home');
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [editingSlider, setEditingSlider] = useState<SliderItem | null>(null);
  const [activeModule, setActiveModule] = useState<string>('hero');
  const [videoUploading, setVideoUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Contact Page için state
  const [activeContactModule, setActiveContactModule] = useState('hero-section');

  // Contact Hero Section için state'ler
  const [editingHeroTitle, setEditingHeroTitle] = useState(false);
  const [heroTitleForm, setHeroTitleForm] = useState('');
  const [editingHeroSubtitle, setEditingHeroSubtitle] = useState(false);
  const [heroSubtitleForm, setHeroSubtitleForm] = useState('');

  // Contact Left Texts (left-title, left-subtitle)
  const [editingLeftTexts, setEditingLeftTexts] = useState(false);
  const [leftTitleForm, setLeftTitleForm] = useState('');
  const [leftSubtitleForm, setLeftSubtitleForm] = useState('');

  // Contact Info için state'ler
  const [editingInfo, setEditingInfo] = useState<ContentItem | null>(null);
  const [infoFormData, setInfoFormData] = useState({
    title: '',
    value: '',
    icon: 'ri-phone-line'
  });

  // Quick Actions için state'ler
  const [editingQuickActions, setEditingQuickActions] = useState(false);
  const [quickActionsFormData, setQuickActionsFormData] = useState({
    call_text: 'Call Now',
    call_url: 'tel:+97141234567',
    whatsapp_text: 'WhatsApp',
    whatsapp_url: 'https://wa.me/97141234567'
  });

  // Social Media için state'ler
  const [editingSocialMedia, setEditingSocialMedia] = useState(false);
  const [socialMediaFormData, setSocialMediaFormData] = useState({
    instagram: '',
    facebook: '',
    youtube: '',
    linkedin: '',
    twitter: ''
  });

  // Map Section için state'ler
  const [editingMapSection, setEditingMapSection] = useState(false);
  const [mapSectionFormData, setMapSectionFormData] = useState({
    title: 'Find Us',
    highlight: 'On The Map',
    map_url: ''
  });

  // Office Locations için state'ler
  const [editingOffice, setEditingOffice] = useState<ContentItem | null>(null);
  const [officeFormData, setOfficeFormData] = useState({
    name: '',
    address: '',
    hours: '',
    phone: '',
    email: '',
    icon: 'ri-building-line'
  });

  // Luxury Collection için state'ler
  const [editingCollection, setEditingCollection] = useState<ContentItem | null>(null);
  const [collectionImages, setCollectionImages] = useState<string[]>([]);

  // Pinnacle Slider için state'ler
  const [editingSlide, setEditingSlide] = useState<any | null>(null);
  const [slideFormData, setSlideFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    button_text: '',
    button_url: '',
    image: '',
    bg_image: ''
  });

  // Awards için state'ler
  const [isAddingAward, setIsAddingAward] = useState(false);
  const [editingAwardId, setEditingAwardId] = useState<string | null>(null);
  const [awardFormData, setAwardFormData] = useState({
    title: '',
    logo_url: '',
  });

  // Contact Section için state'ler
  const [editingContactField, setEditingContactField] = useState<string | null>(null);
  const [contactFormData, setContactFormData] = useState({
    title: '',
    link1_text: '',
    link1_url: '',
    link2_text: '',
    link2_url: '',
    instagram: '',
    facebook: '',
    youtube: '',
    linkedin: '',
    snapchat: '',
    phone: '',
    whatsapp: '',
    email: '',
    pdf_url: ''
  });

  // Contact Section için yeni state'ler
  const [editingContactTitle, setEditingContactTitle] = useState(false);
  const [contactTitleForm, setContactTitleForm] = useState('');
  
  const [editingContactForm, setEditingContactForm] = useState(false);
  const [contactFormDataForm, setContactFormDataForm] = useState({
    form_title: '',
    submit_button_text: '',
    default_country_code: ''
  });

  const [editingContactLinks, setEditingContactLinks] = useState(false);
  const [contactLinksData, setContactLinksData] = useState({
    link1_text: '',
    link1_url: '',
    link2_text: '',
    link2_url: ''
  });

  const [editingContactSocial, setEditingContactSocial] = useState(false);
  const [contactSocialData, setContactSocialData] = useState({
    instagram: '',
    facebook: '',
    youtube: '',
    linkedin: '',
    snapchat: ''
  });

  const [editingContactIcons, setEditingContactIcons] = useState(false);
  const [contactIconsData, setContactIconsData] = useState({
    phone: '',
    whatsapp: '',
    email: '',
    pdf_url: ''
  });

  const [activeResidencesModule, setActiveResidencesModule] = useState('hero');
  const [activeResidencesUnit, setActiveResidencesUnit] = useState<'studio' | '1bedroom' | '2bedroom' | 'penthouse'>('1bedroom');
  const [isResidencesFooterMode, setIsResidencesFooterMode] = useState(false);
  const [showResidencesGalleryBulk, setShowResidencesGalleryBulk] = useState(false);
  const [showResidences3DBulk, setShowResidences3DBulk] = useState(false);
  const [activeAmenitiesModule, setActiveAmenitiesModule] = useState('hero');

  const pages = [
    { id: 'home', name: 'Home' },
    { id: 'brand', name: 'The Golden Tower' },
    { id: 'new-releases', name: 'Residences' },
    { id: 'amenities', name: 'Amenities' },
    { id: 'contact', name: 'Contact' }
  ];

  // Home Hero Text (title/subtitle/button)
  const renderHomeHeroTextModule = () => {
    const item = getContentItem('home', 'hero-text');
    const defaults = {
      title_line1: item?.metadata?.title_line1 || 'Golden',
      title_bold: item?.metadata?.title_bold || 'Tower',
      subtitle: item?.metadata?.subtitle || '',
      button_text: item?.metadata?.button_text || '',
      button_url: item?.metadata?.button_url || ''
    } as any;

    const handleSave = async () => {
      const payload: ContentItem = {
        id: item?.id || `temp-home-hero-text-${Date.now()}`,
        page: 'home',
        section: 'hero-text',
        type: 'text',
        content: 'Home Hero Text',
        order: 0,
        metadata: {
          ...(item?.metadata || {}),
          title_line1: (document.getElementById('home-hero-title1') as HTMLInputElement)?.value || defaults.title_line1,
          title_bold: (document.getElementById('home-hero-titlebold') as HTMLInputElement)?.value || defaults.title_bold,
          subtitle: (document.getElementById('home-hero-subtitle') as HTMLInputElement)?.value || defaults.subtitle,
          button_text: (document.getElementById('home-hero-btntext') as HTMLInputElement)?.value || defaults.button_text,
          button_url: (document.getElementById('home-hero-btnurl') as HTMLInputElement)?.value || defaults.button_url
        }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
      alert('Home Hero Text saved successfully');
    };

    return (
      <div className="space-y-6 bg-white rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Hero Text & Buttons</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title (Line 1)</label>
            <input id="home-hero-title1" defaultValue={defaults.title_line1} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Highlighted Title</label>
            <input id="home-hero-titlebold" defaultValue={defaults.title_bold} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
            <input id="home-hero-subtitle" defaultValue={defaults.subtitle} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
            <input id="home-hero-btntext" defaultValue={defaults.button_text} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button URL</label>
            <input id="home-hero-btnurl" defaultValue={defaults.button_url} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">Save</button>
        </div>
      </div>
    );
  };

  const homeModules = [
    { id: 'hero', name: 'Hero Video', icon: 'ri-video-line' },
    { id: 'hero-text', name: 'Hero Text & Buttons', icon: 'ri-font-size' },
    { id: 'home-intro', name: 'Intro (WHY)', icon: 'ri-question-line' },
    { id: 'projects-slider', name: 'Projects Slider', icon: 'ri-slideshow-line' },
    { id: 'luxury-collection', name: 'Branded Luxury Collection', icon: 'ri-gallery-line' },
    { id: 'home-text1', name: 'Text Block 1', icon: 'ri-file-text-line' },
    { id: 'pinnacle-slider', name: 'Pinnacle Slider', icon: 'ri-image-2-line' },
    { id: 'awards', name: 'Awards & Recognitions', icon: 'ri-award-line' },
    { id: 'awards-gallery', name: 'Awards Gallery', icon: 'ri-image-add-line' },
    { id: 'contact', name: 'Contact Section', icon: 'ri-contacts-line' }
  ];

  const contactModules = [
    { id: 'hero-section', name: 'Hero Section', icon: 'ri-layout-top-line' },
    { id: 'left-section', name: 'Left Title / Subtitle', icon: 'ri-font-size' },
    { id: 'contact-info', name: 'Contact Information', icon: 'ri-contacts-line' },
    { id: 'quick-actions', name: 'Quick Actions', icon: 'ri-flashlight-line' },
    { id: 'social-media', name: 'Social Media', icon: 'ri-share-line' },
    { id: 'map-section', name: 'Map Section', icon: 'ri-map-pin-line' },
    { id: 'office-locations', name: 'Office Locations', icon: 'ri-building-line' }
  ];

  const handleSaveContent = async (item: ContentItem) => {
    try {
      await updateContentItem(item);
      setEditingItem(null);
      await reloadContent();
      alert('Content saved successfully!');
    } catch (error) {
      alert('Failed to save!');
    }
  };

  // Home Intro (WHY)
  const renderHomeIntroModule = () => {
    const introItem = getContentItem('home', 'intro');
    const defaults = {
      title_line1: introItem?.metadata?.title_line1 || 'WHY',
      title_bold: introItem?.metadata?.title_bold || 'Golden Tower?',
      description: introItem?.metadata?.description || '',
      button_text: introItem?.metadata?.button_text || 'REGISTER YOUR INTEREST',
      button_url: introItem?.metadata?.button_url || '/new-releases'
    };

    const handleSave = async () => {
      const payload: ContentItem = {
        id: introItem?.id || `temp-home-intro-${Date.now()}`,
        page: 'home',
        section: 'intro',
        type: 'text',
        content: 'Home Intro',
        order: 0,
        metadata: {
          ...(introItem?.metadata || {}),
          title_line1: (document.getElementById('home-intro-title1') as HTMLInputElement)?.value || defaults.title_line1,
          title_bold: (document.getElementById('home-intro-titlebold') as HTMLInputElement)?.value || defaults.title_bold,
          description: (document.getElementById('home-intro-desc') as HTMLTextAreaElement)?.value || defaults.description,
          button_text: (document.getElementById('home-intro-btntext') as HTMLInputElement)?.value || defaults.button_text,
          button_url: (document.getElementById('home-intro-btnurl') as HTMLInputElement)?.value || defaults.button_url
        }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
      alert('Intro (WHY) saved successfully');
    };

    return (
      <div className="space-y-6 bg-white rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Intro (WHY)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title (Line 1)</label>
            <input id="home-intro-title1" defaultValue={defaults.title_line1} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Highlighted Title</label>
            <input id="home-intro-titlebold" defaultValue={defaults.title_bold} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea id="home-intro-desc" defaultValue={defaults.description} rows={4} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
            <input id="home-intro-btntext" defaultValue={defaults.button_text} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button URL</label>
            <input id="home-intro-btnurl" defaultValue={defaults.button_url} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">Save</button>
        </div>
      </div>
    );
  };

  // Home Text Blocks
  const renderHomeTextBlock = (section: 'text-block-1' | 'text-block-2', title: string) => {
    const item = getContentItem('home', section);
    const defaults = {
      title_line1: item?.metadata?.title_line1 || 'WHY',
      title_bold: item?.metadata?.title_bold || 'Golden Tower?',
      description: item?.metadata?.description || '',
      button_text: item?.metadata?.button_text || '',
      button_url: item?.metadata?.button_url || ''
    };

    const handleSave = async () => {
      const payload: ContentItem = {
        id: item?.id || `temp-${section}-${Date.now()}`,
        page: 'home',
        section,
        type: 'text',
        content: title,
        order: 0,
        metadata: {
          ...(item?.metadata || {}),
          title_line1: (document.getElementById(`${section}-title1`) as HTMLInputElement)?.value || defaults.title_line1,
          title_bold: (document.getElementById(`${section}-titlebold`) as HTMLInputElement)?.value || defaults.title_bold,
          description: (document.getElementById(`${section}-desc`) as HTMLTextAreaElement)?.value || defaults.description,
          button_text: (document.getElementById(`${section}-btntext`) as HTMLInputElement)?.value || defaults.button_text,
          button_url: (document.getElementById(`${section}-btnurl`) as HTMLInputElement)?.value || defaults.button_url
        }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
      alert(`${title} saved successfully`);
    };

    return (
      <div className="space-y-6 bg-white rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title (Line 1)</label>
            <input id={`${section}-title1`} defaultValue={defaults.title_line1} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Highlighted Title</label>
            <input id={`${section}-titlebold`} defaultValue={defaults.title_bold} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea id={`${section}-desc`} defaultValue={defaults.description} rows={4} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
            <input id={`${section}-btntext`} defaultValue={defaults.button_text} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button URL</label>
            <input id={`${section}-btnurl`} defaultValue={defaults.button_url} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">Save</button>
        </div>
      </div>
    );
  };

  const renderHomeTextBlock1Module = () => renderHomeTextBlock('text-block-1', 'Text Block 1');

  // Home Awards Gallery (images slider)
  const renderHomeAwardsGalleryModule = () => {
    const item = getContentItem('home', 'awards-gallery');
    const raw = Array.isArray(item?.metadata?.images) ? item!.metadata.images : [];
    const images: { image: string; caption?: string }[] = raw
      .map((it: any) => {
        if (typeof it === 'string') return { image: it };
        const image = it?.image || it?.url || it?.src || '';
        const caption = it?.caption || it?.text || '';
        return image ? { image, caption } : null;
      })
      .filter(Boolean) as { image: string; caption?: string }[];

    const upsert = async (next: { addImage?: string; removeIndex?: number; updateIndex?: number; updateCaption?: string }) => {
      const nextList = [...images];
      if (typeof next.removeIndex === 'number') nextList.splice(next.removeIndex, 1);
      if (typeof next.updateIndex === 'number') {
        const idx = next.updateIndex;
        if (nextList[idx]) nextList[idx] = { ...nextList[idx], caption: next.updateCaption || '' };
      }
      if (next.addImage) nextList.push({ image: next.addImage, caption: '' });

      const payload: ContentItem = {
        id: item?.id || `temp-home-awards-gallery-${Date.now()}`,
        page: 'home',
        section: 'awards-gallery',
        type: 'gallery',
        content: 'Home Awards Gallery',
        order: 0,
        metadata: { ...(item?.metadata || {}), images: nextList }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
      alert('Awards Gallery saved');
    };

    return (
      <div className="space-y-6 bg-white rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Awards Gallery</h3>
        <MediaUploader type="image" label="Add New Image" onUploadComplete={(url) => upsert({ addImage: url })} />
        <div className="grid grid-cols-1 gap-4 mt-4">
          {images.length > 0 ? (
            images.map((img, idx) => (
              <div key={idx} className="bg-gray-50 rounded border p-3">
                <div className="flex flex-col md:flex-row gap-3">
                  <img src={img.image} className="w-full md:w-64 h-40 rounded border object-cover" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
                    <textarea id={`home-awards-caption-${idx}`} defaultValue={img.caption} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => {
                        const val = (document.getElementById(`home-awards-caption-${idx}`) as HTMLTextAreaElement)?.value || '';
                        upsert({ updateIndex: idx, updateCaption: val });
                      }} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Update</button>
                      <button onClick={() => upsert({ removeIndex: idx })} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Delete</button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-gray-500 text-sm">No images added yet</div>
          )}
        </div>
      </div>
    );
  };

  // Amenities Page Manager
  const renderAmenitiesManager = () => {
    const heroItem = getContentItem('amenities', 'hero');
    const currentHeroImage = heroItem?.image_url || heroItem?.metadata?.image_url || '';
    const currentHeroVideo = heroItem?.metadata?.video_url || heroItem?.video_url || '';
    const currentHeroPoster = heroItem?.metadata?.poster_url || '';

    // Hero Text
    const heroTextItem = getContentItem('amenities', 'hero-text');
    const heroTextDefaults = {
      title_line1: heroTextItem?.metadata?.title_line1 || 'Golden Tower',
      title_bold: heroTextItem?.metadata?.title_bold || 'Luxury Living',
      subtitle: heroTextItem?.metadata?.subtitle || '',
      button_text: heroTextItem?.metadata?.button_text || 'REGISTER YOUR INTEREST',
      button_url: heroTextItem?.metadata?.button_url || '#'
    }

    // WHY / Intro
    const introItem = getContentItem('amenities', 'intro');
    const introDefaults = {
      title_line1: introItem?.metadata?.title_line1 || 'WHY',
      title_bold: introItem?.metadata?.title_bold || 'GOLDEN TOWER?',
      description: introItem?.metadata?.description || '',
      button_text: introItem?.metadata?.button_text || 'REGISTER YOUR INTEREST',
      button_url: introItem?.metadata?.button_url || '#'
    }

    const upsertAmenitiesHero = async (partial: { image_url?: string; video_url?: string; poster_url?: string }) => {
      const payload: ContentItem = {
        id: heroItem?.id || `temp-amenities-hero-${Date.now()}`,
        page: 'amenities',
        section: 'hero',
        type: 'hero',
        content: 'Amenities Hero',
        order: 0,
        image_url: partial.image_url ?? (heroItem?.image_url || heroItem?.metadata?.image_url || ''),
        video_url: partial.video_url ?? (heroItem?.video_url || heroItem?.metadata?.video_url || ''),
        metadata: {
          ...(heroItem?.metadata || {}),
          image_url: partial.image_url ?? (heroItem?.metadata?.image_url || heroItem?.image_url || ''),
          video_url: partial.video_url ?? (heroItem?.metadata?.video_url || heroItem?.video_url || ''),
          poster_url: partial.poster_url ?? (heroItem?.metadata?.poster_url || '')
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Amenities Hero kaydedildi');
    };

    const upsertAmenitiesHeroText = async (data: { title_line1?: string; title_bold?: string; subtitle?: string; button_text?: string; button_url?: string }) => {
      const payload: ContentItem = {
        id: heroTextItem?.id || `temp-amenities-hero-text-${Date.now()}`,
        page: 'amenities',
        section: 'hero-text',
        type: 'text',
        content: 'Amenities Hero Text',
        order: 0,
        metadata: {
          ...(heroTextItem?.metadata || {}),
          title_line1: data.title_line1 ?? heroTextDefaults.title_line1,
          title_bold: data.title_bold ?? heroTextDefaults.title_bold,
          subtitle: data.subtitle ?? heroTextDefaults.subtitle,
          button_text: data.button_text ?? heroTextDefaults.button_text,
          button_url: data.button_url ?? heroTextDefaults.button_url
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Hero text saved successfully!');
    };

    const upsertAmenitiesIntro = async (data: { title_line1?: string; title_bold?: string; description?: string; button_text?: string; button_url?: string }) => {
      const payload: ContentItem = {
        id: introItem?.id || `temp-amenities-intro-${Date.now()}`,
        page: 'amenities',
        section: 'intro',
        type: 'section',
        content: 'Amenities Intro',
        order: 0,
        metadata: {
          ...(introItem?.metadata || {}),
          title_line1: data.title_line1 ?? introDefaults.title_line1,
          title_bold: data.title_bold ?? introDefaults.title_bold,
          description: data.description ?? introDefaults.description,
          button_text: data.button_text ?? introDefaults.button_text,
          button_url: data.button_url ?? introDefaults.button_url
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('WHY section saved successfully!');
    };

    // FAQ
    const faqItem = getContentItem('amenities', 'faq');
    const faqs: { question: string; answer: string }[] = Array.isArray(faqItem?.metadata?.faqs)
      ? faqItem!.metadata.faqs
      : [];
    const upsertAmenitiesFaq = async (nextFaqs: { question: string; answer: string }[]) => {
      const payload: ContentItem = {
        id: faqItem?.id || `temp-amenities-faq-${Date.now()}`,
        page: 'amenities',
        section: 'faq',
        type: 'faq',
        content: 'Amenities FAQ',
        order: 0,
        metadata: {
          ...(faqItem?.metadata || {}),
          faqs: nextFaqs
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('FAQ saved successfully!');
    };

    // Footer
    const amenFooterItem = getContentItem('amenities', 'footer');
    const amenFooterDefaults = {
      bg_image_url: amenFooterItem?.metadata?.bg_image_url || amenFooterItem?.image_url || '',
      left_title_line1: amenFooterItem?.metadata?.left_title_line1 || 'Connect with',
      left_title_bold: amenFooterItem?.metadata?.left_title_bold || 'the brand',
      left_link1_text: amenFooterItem?.metadata?.left_link1_text || 'FIND A SALES BOUTIQUE',
      left_link1_url: amenFooterItem?.metadata?.left_link1_url || '#',
      left_link2_text: amenFooterItem?.metadata?.left_link2_text || 'DISCOVER THE COLLECTION',
      left_link2_url: amenFooterItem?.metadata?.left_link2_url || '#',
      form_title_line1: amenFooterItem?.metadata?.form_title_line1 || 'Express',
      form_title_bold: amenFooterItem?.metadata?.form_title_bold || 'Your Interest',
      submit_button_text: amenFooterItem?.metadata?.submit_button_text || 'MEET OUR BRAND AMBASSADOR',
      terms_text: amenFooterItem?.metadata?.terms_text || 'By submitting, you agree to our terms & conditions*'
    };
    const upsertAmenitiesFooter = async (data: Partial<typeof amenFooterDefaults>) => {
      const payload: ContentItem = {
        id: amenFooterItem?.id || `temp-amenities-footer-${Date.now()}`,
        page: 'amenities',
        section: 'footer',
        type: 'footer',
        content: 'Amenities Footer',
        order: 0,
        image_url: data.bg_image_url ?? (amenFooterItem?.image_url || amenFooterItem?.metadata?.bg_image_url || ''),
        metadata: {
          ...(amenFooterItem?.metadata || {}),
          bg_image_url: data.bg_image_url ?? amenFooterDefaults.bg_image_url,
          left_title_line1: data.left_title_line1 ?? amenFooterDefaults.left_title_line1,
          left_title_bold: data.left_title_bold ?? amenFooterDefaults.left_title_bold,
          left_link1_text: data.left_link1_text ?? amenFooterDefaults.left_link1_text,
          left_link1_url: data.left_link1_url ?? amenFooterDefaults.left_link1_url,
          left_link2_text: data.left_link2_text ?? amenFooterDefaults.left_link2_text,
          left_link2_url: data.left_link2_url ?? amenFooterDefaults.left_link2_url,
          form_title_line1: data.form_title_line1 ?? amenFooterDefaults.form_title_line1,
          form_title_bold: data.form_title_bold ?? amenFooterDefaults.form_title_bold,
          submit_button_text: data.submit_button_text ?? amenFooterDefaults.submit_button_text,
          terms_text: data.terms_text ?? amenFooterDefaults.terms_text
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Footer saved successfully!');
    };

    const renderAmenitiesSectionEditor = (sectionKey: 'section1' | 'section2' | 'section3' | 'section4' | 'section5' | 'section6' | 'gallery') => {
      const item = getContentItem('amenities', sectionKey);
      const raw = Array.isArray(item?.metadata?.images) ? item!.metadata.images : [];
      const images: { image: string; text: string }[] = raw
        .map((it: any) => {
          if (typeof it === 'string') return { image: it, text: '' };
          const image = it?.image || it?.url || it?.src || '';
          const text = it?.text || it?.caption || '';
          return image ? { image, text } : null;
        })
        .filter(Boolean) as { image: string; text: string }[];

      const upsertSection = async (next: { addImage?: string; removeIndex?: number; updateIndex?: number; updateText?: string }) => {
        const nextImages = [...images];
        if (typeof next.removeIndex === 'number') nextImages.splice(next.removeIndex, 1);
        if (typeof next.updateIndex === 'number') {
          const idx = next.updateIndex;
          if (nextImages[idx]) nextImages[idx] = { ...nextImages[idx], text: next.updateText || '' };
        }
        if (next.addImage) nextImages.push({ image: next.addImage, text: '' });

        const payload: ContentItem = {
          id: item?.id || `temp-amenities-${sectionKey}-${Date.now()}`,
          page: 'amenities',
          section: sectionKey,
          type: 'section',
          content: 'Amenities Section',
          order: 0,
          metadata: {
            ...(item?.metadata || {}),
            images: nextImages
          }
        };
        await updateContentItem(payload);
        await reloadContent();
        alert(`${sectionKey} kaydedildi`);
      };

      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg p-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Images</h4>
            <MediaUploader type="image" label="Add New Image" onUploadComplete={(url) => upsertSection({ addImage: url })} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {images.length > 0 ? (
                images.map((img, idx) => (
                  <div key={idx} className="bg-gray-50 rounded border p-3">
                    <div className="flex gap-3">
                      <img src={img.image} alt={`Section image ${idx + 1}`} className="w-40 h-28 object-cover rounded border" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image Caption</label>
                        <textarea id={`${sectionKey}-caption-${idx}`} defaultValue={img.text} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => {
                            const val = (document.getElementById(`${sectionKey}-caption-${idx}`) as HTMLTextAreaElement)?.value || '';
                            upsertSection({ updateIndex: idx, updateText: val });
                          }} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Update</button>
                          <button onClick={() => upsertSection({ removeIndex: idx })} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Delete</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-sm">No images added yet</div>
              )}
            </div>
          </div>
        </div>
      );
    };

    const renderAmenitiesVideoSliderEditor = () => {
      const item = getContentItem('amenities', 'video-slider');
      const raw = Array.isArray(item?.metadata?.videos) ? item!.metadata.videos : [];
      const videos: { video: string; caption?: string }[] = raw
        .map((it: any) => {
          if (typeof it === 'string') return { video: it };
          const video = it?.video || it?.url || '';
          const caption = it?.caption || '';
          return video ? { video, caption } : null;
        })
        .filter(Boolean) as { video: string; caption?: string }[];

      const upsert = async (next: { addVideo?: string; removeIndex?: number; updateIndex?: number; updateCaption?: string }) => {
        const nextList = [...videos];
        if (typeof next.removeIndex === 'number') nextList.splice(next.removeIndex, 1);
        if (typeof next.updateIndex === 'number') {
          const idx = next.updateIndex;
          if (nextList[idx]) nextList[idx] = { ...nextList[idx], caption: next.updateCaption || '' };
        }
        if (next.addVideo) nextList.push({ video: next.addVideo, caption: '' });

        const payload: ContentItem = {
          id: item?.id || `temp-amenities-video-slider-${Date.now()}`,
          page: 'amenities',
          section: 'video-slider',
          type: 'section',
          content: 'Amenities Video Slider',
          order: 0,
          metadata: {
            ...(item?.metadata || {}),
            videos: nextList
          }
        };
        await updateContentItem(payload);
        await reloadContent();
        alert('Video slider saved successfully!');
      };

      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg p-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Videolar</h4>
            <MediaUploader type="video" label="Yeni Video Ekle" onUploadComplete={(url) => upsert({ addVideo: url })} />
            <div className="grid grid-cols-1 gap-4 mt-4">
              {videos.length > 0 ? (
                videos.map((v, idx) => (
                  <div key={idx} className="bg-gray-50 rounded border p-3">
                    <div className="flex flex-col md:flex-row gap-3">
                      <video src={v.video} className="w-full md:w-64 h-40 rounded border object-cover" controls />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Video Caption</label>
                        <textarea id={`amen-video-caption-${idx}`} defaultValue={v.caption} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => {
                            const val = (document.getElementById(`amen-video-caption-${idx}`) as HTMLTextAreaElement)?.value || '';
                            upsert({ updateIndex: idx, updateCaption: val });
                          }} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Update</button>
                          <button onClick={() => upsert({ removeIndex: idx })} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Delete</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-sm">No videos added yet</div>
              )}
            </div>
          </div>
        </div>
      );
    };

    function renderContactLeftSection() {
      const leftTitleItem = getContentItem('contact', 'left-title');
      const leftSubtitleItem = getContentItem('contact', 'left-subtitle');

      const handleSaveLeft = async () => {
        try {
          const titleItem: ContentItem = {
            id: leftTitleItem?.id || `temp-contact-left-title-${Date.now()}`,
            page: 'contact',
            section: 'left-title',
            type: 'text',
            content: leftTitleForm,
            order: 0
          };
          const subtitleItem: ContentItem = {
            id: leftSubtitleItem?.id || `temp-contact-left-subtitle-${Date.now()}`,
            page: 'contact',
            section: 'left-subtitle',
            type: 'text',
            content: leftSubtitleForm,
            order: 0
          };

          await updateContentItem(titleItem);
          await updateContentItem(subtitleItem);
          setEditingLeftTexts(false);
          await reloadContent();
          alert('Left title and subtitle saved successfully!');
        } catch (error) {
          alert('Failed to save!');
        }
      };

      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className="ri-font-size mr-2"></i>
                Left Title / Subtitle
              </h3>
              {!editingLeftTexts && (
                <button
                  onClick={() => {
                    setEditingLeftTexts(true);
                    setLeftTitleForm(leftTitleItem?.content || '');
                    setLeftSubtitleForm(leftSubtitleItem?.content || '');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Edit
                </button>
              )}
            </div>

            {editingLeftTexts ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Left Title (Each line will appear separately)</label>
                  <textarea
                    value={leftTitleForm}
                    onChange={(e) => setLeftTitleForm(e.target.value)}
                    placeholder={"We’re Here to\nAssist You"}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                  <p className="text-sm text-gray-500 mt-1">💡 Enter each line on a new line. E.g.: We’re Here to (Enter) Assist You</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Left Subtitle</label>
                  <textarea
                    value={leftSubtitleForm}
                    onChange={(e) => setLeftSubtitleForm(e.target.value)}
                    placeholder="Whether you’re seeking more information..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={handleSaveLeft} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer">Save</button>
                  <button onClick={() => setEditingLeftTexts(false)} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-2xl font-light text-gray-900 whitespace-pre-line">{leftTitleItem?.content || '—'}</div>
                <div className="text-gray-700">{leftSubtitleItem?.content || '—'}</div>
              </div>
            )}
          </div>
        </div>
      );
    }

    function renderContactLeftSection() {
      const leftTitleItem = getContentItem('contact', 'left-title');
      const leftSubtitleItem = getContentItem('contact', 'left-subtitle');

      const handleSaveLeft = async () => {
        try {
          const titleItem: ContentItem = {
            id: leftTitleItem?.id || `temp-contact-left-title-${Date.now()}`,
            page: 'contact',
            section: 'left-title',
            type: 'text',
            content: leftTitleForm,
            order: 0
          };
          const subtitleItem: ContentItem = {
            id: leftSubtitleItem?.id || `temp-contact-left-subtitle-${Date.now()}`,
            page: 'contact',
            section: 'left-subtitle',
            type: 'text',
            content: leftSubtitleForm,
            order: 0
          };

          await updateContentItem(titleItem);
          await updateContentItem(subtitleItem);
          setEditingLeftTexts(false);
          await reloadContent();
          alert('Left title and subtitle saved successfully!');
        } catch (error) {
          alert('Failed to save!');
        }
      };

      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className="ri-font-size mr-2"></i>
                Left Title / Subtitle
              </h3>
              {!editingLeftTexts && (
                <button
                  onClick={() => {
                    setEditingLeftTexts(true);
                    setLeftTitleForm(leftTitleItem?.content || '');
                    setLeftSubtitleForm(leftSubtitleItem?.content || '');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Edit
                </button>
              )}
            </div>

            {editingLeftTexts ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Left Title (Each line will appear separately)</label>
                  <textarea
                    value={leftTitleForm}
                    onChange={(e) => setLeftTitleForm(e.target.value)}
                    placeholder={"We’re Here to\nAssist You"}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                  <p className="text-sm text-gray-500 mt-1">💡 Enter each line on a new line. E.g.: We’re Here to (Enter) Assist You</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Left Subtitle</label>
                  <textarea
                    value={leftSubtitleForm}
                    onChange={(e) => setLeftSubtitleForm(e.target.value)}
                    placeholder="Whether you’re seeking more information..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={handleSaveLeft} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer">Save</button>
                  <button onClick={() => setEditingLeftTexts(false)} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-2xl font-light text-gray-900 whitespace-pre-line">{leftTitleItem?.content || '—'}</div>
                <div className="text-gray-700">{leftSubtitleItem?.content || '—'}</div>
              </div>
            )}
          </div>
        </div>
      );
    };

    function leftSectionView() {
      const leftTitleItem = getContentItem('contact', 'left-title');
      const leftSubtitleItem = getContentItem('contact', 'left-subtitle');

      const handleSaveLeft = async () => {
        try {
          const titleItem: ContentItem = {
            id: leftTitleItem?.id || `temp-contact-left-title-${Date.now()}`,
            page: 'contact',
            section: 'left-title',
            type: 'text',
            content: leftTitleForm,
            order: 0
          };
          const subtitleItem: ContentItem = {
            id: leftSubtitleItem?.id || `temp-contact-left-subtitle-${Date.now()}`,
            page: 'contact',
            section: 'left-subtitle',
            type: 'text',
            content: leftSubtitleForm,
            order: 0
          };

          await updateContentItem(titleItem);
          await updateContentItem(subtitleItem);
          setEditingLeftTexts(false);
          await reloadContent();
        } catch (error) {
          alert('Failed to save!');
        }
      };

      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className="ri-font-size mr-2"></i>
                Left Title / Subtitle
              </h3>
              {!editingLeftTexts && (
                <button
                  onClick={() => {
                    setEditingLeftTexts(true);
                    setLeftTitleForm(leftTitleItem?.content || '');
                    setLeftSubtitleForm(leftSubtitleItem?.content || '');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Edit
                </button>
              )}
            </div>

            {editingLeftTexts ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Left Title (Each line will appear separately)</label>
                  <textarea
                    value={leftTitleForm}
                    onChange={(e) => setLeftTitleForm(e.target.value)}
                    placeholder={"We’re Here to\nAssist You"}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Left Subtitle</label>
                  <textarea
                    value={leftSubtitleForm}
                    onChange={(e) => setLeftSubtitleForm(e.target.value)}
                    placeholder="Whether you’re seeking more information..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={handleSaveLeft} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer">Save</button>
                  <button onClick={() => setEditingLeftTexts(false)} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-2xl font-light text-gray-900 whitespace-pre-line">{leftTitleItem?.content || '—'}</div>
                <div className="text-gray-700">{leftSubtitleItem?.content || '—'}</div>
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="grid grid-cols-6 gap-4">
        {/* Module Sidebar */}
        <div className="col-span-1 space-y-2">
          {[
            { id: 'hero', name: 'Hero Media', icon: 'ri-video-line' },
            { id: 'hero-text', name: 'Hero Text & Button', icon: 'ri-font-size' },
            { id: 'intro', name: 'Intro (WHY)', icon: 'ri-question-line' },
            { id: 'section1', name: 'Section 1', icon: 'ri-image-2-line' },
            { id: 'section2', name: 'Section 2', icon: 'ri-image-2-line' },
            { id: 'section3', name: 'Section 3', icon: 'ri-image-2-line' },
            { id: 'section4', name: 'Section 4', icon: 'ri-image-2-line' },
            { id: 'section5', name: 'Section 5', icon: 'ri-image-2-line' },
            { id: 'section6', name: 'Section 6', icon: 'ri-image-2-line' },
            { id: 'video-slider', name: 'Video Slider', icon: 'ri-movie-line' },
            { id: 'gallery', name: 'Gallery', icon: 'ri-image-line' },
            { id: 'faq', name: 'FAQ', icon: 'ri-question-answer-line' },
            { id: 'footer', name: 'Footer', icon: 'ri-layout-bottom-2-line' }
          ].map((m) => (
            <button
              key={m.id}
              onClick={() => setActiveAmenitiesModule(m.id)}
              className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                activeAmenitiesModule === m.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className={`${m.icon} mr-2`}></i>
              <span className="text-sm">{m.name}</span>
            </button>
          ))}
        </div>

        {/* Module Content */}
        <div className="col-span-5 space-y-6">
          {activeAmenitiesModule === 'hero' && (
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Hero Media</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <MediaUploader type="video" currentUrl={currentHeroVideo} label="Hero Video" onUploadComplete={(url) => upsertAmenitiesHero({ video_url: url })} />
                <MediaUploader type="image" currentUrl={currentHeroPoster} label="Poster Image (for video)" onUploadComplete={(url) => upsertAmenitiesHero({ poster_url: url })} />
              </div>
              <div className="mt-6">
                <MediaUploader type="image" currentUrl={currentHeroImage} label="Hero Image (used if no video)" onUploadComplete={(url) => upsertAmenitiesHero({ image_url: url })} />
              </div>
              {currentHeroVideo && (
                <div className="mt-4">
                  <button onClick={() => upsertAmenitiesHero({ video_url: '' })} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Remove Video</button>
                </div>
              )}
            </div>
          )}

          {activeAmenitiesModule === 'hero-text' && (
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Hero Text & Button</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title (line 1)</label>
                  <input id="amen-hero-title-line1" type="text" defaultValue={heroTextDefaults.title_line1} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title (bold)</label>
                  <input id="amen-hero-title-bold" type="text" defaultValue={heroTextDefaults.title_bold} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                  <input id="amen-hero-subtitle" type="text" defaultValue={heroTextDefaults.subtitle} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                  <input id="amen-hero-button-text" type="text" defaultValue={heroTextDefaults.button_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buton URL</label>
                  <input id="amen-hero-button-url" type="text" defaultValue={heroTextDefaults.button_url} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
              </div>
              <div className="mt-4">
                <button onClick={() => upsertAmenitiesHeroText({
                  title_line1: (document.getElementById('amen-hero-title-line1') as HTMLInputElement)?.value || '',
                  title_bold: (document.getElementById('amen-hero-title-bold') as HTMLInputElement)?.value || '',
                  subtitle: (document.getElementById('amen-hero-subtitle') as HTMLInputElement)?.value || '',
                  button_text: (document.getElementById('amen-hero-button-text') as HTMLInputElement)?.value || '',
                  button_url: (document.getElementById('amen-hero-button-url') as HTMLInputElement)?.value || ''
                })} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
              </div>
            </div>
          )}

          {activeAmenitiesModule === 'intro' && (
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Intro (WHY) Section</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title (line 1)</label>
                  <input id="amen-intro-title-line1" type="text" defaultValue={introDefaults.title_line1} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title (bold)</label>
                  <input id="amen-intro-title-bold" type="text" defaultValue={introDefaults.title_bold} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea id="amen-intro-description" rows={4} defaultValue={introDefaults.description} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                  <input id="amen-intro-button-text" type="text" defaultValue={introDefaults.button_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buton URL</label>
                  <input id="amen-intro-button-url" type="text" defaultValue={introDefaults.button_url} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
              </div>
              <div className="mt-4">
                <button onClick={() => upsertAmenitiesIntro({
                  title_line1: (document.getElementById('amen-intro-title-line1') as HTMLInputElement)?.value || '',
                  title_bold: (document.getElementById('amen-intro-title-bold') as HTMLInputElement)?.value || '',
                  description: (document.getElementById('amen-intro-description') as HTMLTextAreaElement)?.value || '',
                  button_text: (document.getElementById('amen-intro-button-text') as HTMLInputElement)?.value || '',
                  button_url: (document.getElementById('amen-intro-button-url') as HTMLInputElement)?.value || ''
                })} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
              </div>
            </div>
          )}

          {activeAmenitiesModule === 'section1' && renderAmenitiesSectionEditor('section1')}
          {activeAmenitiesModule === 'section2' && renderAmenitiesSectionEditor('section2')}
          {activeAmenitiesModule === 'section3' && renderAmenitiesSectionEditor('section3')}
          {activeAmenitiesModule === 'section4' && renderAmenitiesSectionEditor('section4')}
          {activeAmenitiesModule === 'section5' && renderAmenitiesSectionEditor('section5')}
          {activeAmenitiesModule === 'section6' && renderAmenitiesSectionEditor('section6')}
          {activeAmenitiesModule === 'video-slider' && renderAmenitiesVideoSliderEditor()}
          {activeAmenitiesModule === 'gallery' && renderAmenitiesSectionEditor('gallery')}

          {activeAmenitiesModule === 'faq' && (
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Frequently Asked Questions</h3>
              <div className="space-y-4">
                {faqs.length > 0 ? (
                  faqs.map((f, idx) => (
                    <div key={idx} className="border rounded p-4 bg-gray-50">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                      <input id={`amen-faq-q-${idx}`} type="text" defaultValue={f.question} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white mb-3" />
                      <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                      <textarea id={`amen-faq-a-${idx}`} rows={3} defaultValue={f.answer} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => {
                            const q = (document.getElementById(`amen-faq-q-${idx}`) as HTMLInputElement)?.value || '';
                            const a = (document.getElementById(`amen-faq-a-${idx}`) as HTMLTextAreaElement)?.value || '';
                            const copy = [...faqs];
                            copy[idx] = { question: q, answer: a };
                            upsertAmenitiesFaq(copy);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          Update
                        </button>
                        <button
                          onClick={() => {
                            const copy = faqs.filter((_, i) => i !== idx);
                            upsertAmenitiesFaq(copy);
                          }}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                        >
                          Sil
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-500 text-sm">No questions added yet</div>
                )}
                <div className="border rounded p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-2">Add New Question</h4>
                  <input id="amen-faq-new-q" type="text" placeholder="Soru" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white mb-3" />
                  <textarea id="amen-faq-new-a" rows={3} placeholder="Cevap" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                  <div className="mt-3">
                    <button
                      onClick={() => {
                        const q = (document.getElementById('amen-faq-new-q') as HTMLInputElement)?.value || '';
                        const a = (document.getElementById('amen-faq-new-a') as HTMLTextAreaElement)?.value || '';
                        if (!q || !a) return alert('Question and answer are required');
                        upsertAmenitiesFaq([...faqs, { question: q, answer: a }]);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Ekle
                    </button>
                  </div>
                </div>

                {/* Hero Text & Button */}
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Hero Text & Button</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title (line 1)</label>
                      <input id="res-hero-title-line1" type="text" defaultValue={residencesHeroDefaults.title_line1} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title (bold)</label>
                      <input id="res-hero-title-bold" type="text" defaultValue={residencesHeroDefaults.title_bold} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                      <input id="res-hero-subtitle" type="text" defaultValue={residencesHeroDefaults.subtitle} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                      <input id="res-hero-btntext" type="text" defaultValue={residencesHeroDefaults.button_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Buton URL</label>
                      <input id="res-hero-btnurl" type="text" defaultValue={residencesHeroDefaults.button_url} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => {
                        const data = {
                          title_line1: (document.getElementById('res-hero-title-line1') as HTMLInputElement)?.value || '',
                          title_bold: (document.getElementById('res-hero-title-bold') as HTMLInputElement)?.value || '',
                          subtitle: (document.getElementById('res-hero-subtitle') as HTMLInputElement)?.value || '',
                          button_text: (document.getElementById('res-hero-btntext') as HTMLInputElement)?.value || '',
                          button_url: (document.getElementById('res-hero-btnurl') as HTMLInputElement)?.value || ''
                        };
                        upsertResidencesHeroText(data);
                      }}
                      className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Kaydet
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeAmenitiesModule === 'footer' && (
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Footer Content</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <MediaUploader type="image" currentUrl={amenFooterDefaults.bg_image_url} label="Background Image" onUploadComplete={(url) => upsertAmenitiesFooter({ bg_image_url: url })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Left Title (line 1)</label>
                  <input id="amen-footer-left-title-line1" type="text" defaultValue={amenFooterDefaults.left_title_line1} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Left Title (bold)</label>
                  <input id="amen-footer-left-title-bold" type="text" defaultValue={amenFooterDefaults.left_title_bold} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Left Link 1 Text</label>
                  <input id="amen-footer-left-link1-text" type="text" defaultValue={amenFooterDefaults.left_link1_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Left Link 1 URL</label>
                  <input id="amen-footer-left-link1-url" type="text" defaultValue={amenFooterDefaults.left_link1_url} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Left Link 2 Text</label>
                  <input id="amen-footer-left-link2-text" type="text" defaultValue={amenFooterDefaults.left_link2_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Left Link 2 URL</label>
                  <input id="amen-footer-left-link2-url" type="text" defaultValue={amenFooterDefaults.left_link2_url} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Form Title (line 1)</label>
                  <input id="amen-footer-form-title-line1" type="text" defaultValue={amenFooterDefaults.form_title_line1} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Form Title (bold)</label>
                  <input id="amen-footer-form-title-bold" type="text" defaultValue={amenFooterDefaults.form_title_bold} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Form Button Text</label>
                  <input id="amen-footer-submit-button-text" type="text" defaultValue={amenFooterDefaults.submit_button_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions Text</label>
                  <input id="amen-footer-terms-text" type="text" defaultValue={amenFooterDefaults.terms_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    const data = {
                      left_title_line1: (document.getElementById('amen-footer-left-title-line1') as HTMLInputElement)?.value || '',
                      left_title_bold: (document.getElementById('amen-footer-left-title-bold') as HTMLInputElement)?.value || '',
                      left_link1_text: (document.getElementById('amen-footer-left-link1-text') as HTMLInputElement)?.value || '',
                      left_link1_url: (document.getElementById('amen-footer-left-link1-url') as HTMLInputElement)?.value || '',
                      left_link2_text: (document.getElementById('amen-footer-left-link2-text') as HTMLInputElement)?.value || '',
                      left_link2_url: (document.getElementById('amen-footer-left-link2-url') as HTMLInputElement)?.value || '',
                      form_title_line1: (document.getElementById('amen-footer-form-title-line1') as HTMLInputElement)?.value || '',
                      form_title_bold: (document.getElementById('amen-footer-form-title-bold') as HTMLInputElement)?.value || '',
                      submit_button_text: (document.getElementById('amen-footer-submit-button-text') as HTMLInputElement)?.value || '',
                      terms_text: (document.getElementById('amen-footer-terms-text') as HTMLInputElement)?.value || ''
                    };
                    upsertAmenitiesFooter(data);
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Kaydet
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleSaveSlider = async (item: SliderItem) => {
    try {
      await updateSliderItem(item);
      setEditingSlider(null);
      await reloadContent(); // Reload content
      alert('Slider saved successfully!');
    } catch (error) {
      alert('Failed to save!');
    }
  };

  const handleDeleteSlider = async (id: string) => {
    if (confirm('Are you sure you want to delete this slider item?')) {
      try {
        await deleteSliderItem(id);
        await reloadContent(); // Reload content
        alert('Slider deleted!');
      } catch (error) {
        alert('Failed to delete!');
      }
    }
  };

  const createNewSlider = (page: string, section: string) => {
    const newSlider: SliderItem = {
      id: `${page}-${section}-${Date.now()}`,
      page,
      title: 'New Title',
      subtitle: 'New Subtitle',
      image_url: '',
      bg_image_url: '',
      button_text: 'Explore',
      button_url: '#',
      order: sliders.filter(s => s.page === page).length
    };
    setEditingSlider(newSlider);
  };

  const handleVideoUpload = async (file: File) => {
    try {
      setVideoUploading(true);
      setUploadProgress(20);
      
      // Dosya boyutu kontrolü - 50MB maksimum (Supabase limit)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        alert(`Video file is too large! Maximum 50MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        setVideoUploading(false);
        return;
      }

      setUploadProgress(40);

      // Video sıkıştırma fonksiyonu
      const compressVideo = async (videoFile: File): Promise<File> => {
        return new Promise((resolve, reject) => {
          const video = document.createElement('video');
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          video.onloadedmetadata = () => {
            // Video boyutlarını küçült
            const maxWidth = 1280;
            const maxHeight = 720;
            let { videoWidth: width, videoHeight: height } = video;
            
            // Orantılı küçültme
            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height);
              width = Math.floor(width * ratio);
              height = Math.floor(height * ratio);
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // Video frame'ini canvas'a çiz
            video.currentTime = 0;
          };
          
          video.onseeked = () => {
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              
              // Canvas'ı blob'a çevir (JPEG formatında daha küçük)
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    // Eğer sıkıştırma başarılı ve dosya küçükse
                    if (blob.size < videoFile.size * 0.7) {
                      const timestamp = Date.now();
                      const compressedFile = new File(
                        [blob], 
                        `compressed-video-${timestamp}.mp4`, 
                        { type: 'video/mp4' }
                      );
                      resolve(compressedFile);
                    } else {
                      // Sıkıştırma yeterli değilse orijinal dosyayı kullan
                      resolve(videoFile);
                    }
                  } else {
                    resolve(videoFile);
                  }
                },
                'video/mp4',
                0.6 // %60 kalite
              );
            } else {
              resolve(videoFile);
            }
          };
          
          video.onerror = () => resolve(videoFile);
          video.src = URL.createObjectURL(videoFile);
        });
      };

      let processedFile = file;
      
      // Eğer dosya 25MB'dan büyükse sıkıştır
      if (file.size > 25 * 1024 * 1024) {
        console.log('Compressing video...');
        setUploadProgress(50);
        processedFile = await compressVideo(file);
        console.log(`Compression result: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
      }
      
      // Final size check
      if (processedFile.size > maxSize) {
        alert(`Video is still too large after compression! Please choose a smaller video. Processed size: ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
        setVideoUploading(false);
        return;
      }

      setUploadProgress(70);

      // Generate unique file name
      const timestamp = Date.now();
      const fileName = `hero-video-${timestamp}.mp4`;
      
      console.log('Uploading video:', fileName, `${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);

      // Upload to API
      const data = await api.upload('/media/upload', processedFile);
      if (!data.success) throw new Error(data.error || 'Video upload error');
      
      setUploadProgress(90);
      const videoUrl = data.url;
      
      // Save to database
      const heroVideoItem = {
        id: `temp-home-hero-video-${timestamp}`,
        page: 'home',
        section: 'hero-video',
        type: 'video',
        content: videoUrl,
        video_url: videoUrl,
        order: 0,
        title: 'Hero Video',
        page_key: `home-hero-video-${timestamp}`
      };
      
      await updateContentItem(heroVideoItem);
      
      setUploadProgress(100);
      
      // Reload content
      await reloadContent();
      
      alert('Video uploaded successfully!');
      console.log('Video uploaded successfully:', videoUrl);
    } catch (error) {
      console.error('Video upload error:', error);
      alert('An error occurred during video upload!');
    } finally {
      setVideoUploading(false);
      setUploadProgress(0);
    }
  };

  const renderHeroVideoModule = () => {
    const heroVideo = getContentItem('home', 'hero-video');
    const currentVideoUrl = heroVideo?.video_url || heroVideo?.content || '';

    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-video-line mr-2"></i>
          Hero Video Module
        </h3>
        
        {/* Current Video URL */}
        {currentVideoUrl && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Video URL:
            </label>
            <div className="text-sm text-gray-600 break-all bg-white p-2 rounded border">
              {currentVideoUrl}
            </div>
            {/* Video Preview */}
            <div className="mt-3">
              <video 
                src={currentVideoUrl} 
                className="w-full max-w-md h-32 object-cover rounded border"
                controls
                muted
              >
                Video preview is not supported.
              </video>
            </div>
          </div>
        )}

        {/* Video Upload */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload New Video
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleVideoUpload(file);
                }
              }}
              disabled={videoUploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {videoUploading && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-600">
                  {uploadProgress < 40 ? 'Preparing...' : uploadProgress < 70 ? 'Uploading...' : uploadProgress < 90 ? 'Processing...' : 'Finalizing...'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">{uploadProgress}%</p>
            </div>
          )}
        </div>
        
        <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded">
          <p className="font-medium mb-1">📌 Important Information:</p>
          <ul className="space-y-1">
            <li>• Maximum file size: 75MB</li>
            <li>• Supported formats: MP4, MOV, AVI</li>
            <li>• Video will automatically be displayed on the homepage</li>
            <li>• The page will automatically refresh once the upload is complete</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderProjectsSlider = () => {
    // Filter sliders coming from database
    const projectSliders = sliders.filter(s => s.id && !s.id.includes('home-projects-'));

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center">
            <i className="ri-slideshow-line mr-2"></i>
            Projects Slider Module
          </h3>
          <button
            onClick={() => createNewSlider('home-projects', 'projects')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
          >
            <i className="ri-add-line mr-2"></i>Add New Slide
          </button>
        </div>

        {/* Existing Sliders */}
        <div className="grid gap-4">
          {projectSliders.length > 0 ? (
            projectSliders.map((slider, index) => (
              <div key={slider.id} className="bg-white p-6 rounded-lg border shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                      Slide {index + 1}
                    </span>
                    <h4 className="font-medium text-lg">{slider.title}</h4>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingSlider(slider)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center"
                    >
                      <i className="ri-edit-line mr-1"></i>Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSlider(slider.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm flex items-center"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>Delete
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Image Preview */}
                  <div>
                    {slider.image_url ? (
                      <img 
                        src={slider.image_url} 
                        alt={slider.title} 
                        className="w-full h-48 object-cover rounded border"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gray-200 rounded border flex items-center justify-center">
                        <i className="ri-image-line text-gray-400 text-2xl"></i>
                        <span className="ml-2 text-gray-500">No Image</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Slide Information */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title:</label>
                      <p className="text-gray-900 font-medium">{slider.title}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle:</label>
                      <p className="text-gray-600">{slider.subtitle}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Button Text:</label>
                      <p className="text-gray-600">{slider.button_text || 'DISCOVER THE COLLECTION'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Button Link:</label>
                      <p className="text-gray-600 break-all">{slider.button_url || '#'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order:</label>
                      <p className="text-gray-600">{slider.order + 1}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-gray-50 p-8 rounded-lg border-2 border-dashed border-gray-300 text-center">
              <i className="ri-slideshow-line text-gray-400 text-4xl mb-4"></i>
              <h4 className="text-lg font-medium text-gray-700 mb-2">No Slides Added Yet</h4>
              <p className="text-gray-500 mb-4">Click "Add New Slide" above to add your first slide.</p>
              <button
                onClick={() => createNewSlider('home-projects', 'projects')}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center mx-auto"
              >
                <i className="ri-add-line mr-2"></i>Add First Slide
              </button>
            </div>
          )}
        </div>

        {/* Slide Edit Modal */}
        {editingSlider && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold flex items-center">
                  <i className="ri-edit-line mr-2"></i>
                  {editingSlider.id.includes('home-projects-') ? 'Add New Slide' : 'Edit Slide'}
                </h3>
                <button
                  onClick={() => setEditingSlider(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Side - Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={editingSlider.title}
                      onChange={(e) => setEditingSlider({ ...editingSlider, title: e.target.value })}
                      placeholder="e.g. Bugatti Residences"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      style={{ color: '#111827 !important' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtitle *
                    </label>
                    <input
                      type="text"
                      value={editingSlider.subtitle}
                      onChange={(e) => setEditingSlider({ ...editingSlider, subtitle: e.target.value })}
                      placeholder="e.g. by Golden Tower"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      style={{ color: '#111827 !important' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={editingSlider.button_text || ''}
                      onChange={(e) => setEditingSlider({ ...editingSlider, button_text: e.target.value })}
                      placeholder="DISCOVER THE COLLECTION"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      style={{ color: '#111827 !important' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Button Link
                    </label>
                    <input
                      type="text"
                      value={editingSlider.button_url || ''}
                      onChange={(e) => setEditingSlider({ ...editingSlider, button_url: e.target.value })}
                      placeholder="#"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      style={{ color: '#111827 !important' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order
                    </label>
                    <input
                      type="number"
                      value={editingSlider.order}
                      onChange={(e) => setEditingSlider({ ...editingSlider, order: parseInt(e.target.value) || 0 })}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      style={{ color: '#111827 !important' }}
                    />
                  </div>
                </div>

                {/* Right Side - Image Upload */}
                <div className="space-y-4">
                  <MediaUploader
                    type="image"
                    currentUrl={editingSlider.image_url}
                    label="Slide Image"
                    onUploadComplete={(url) => setEditingSlider({ ...editingSlider, image_url: url })}
                  />
                  
                  {/* Image Preview */}
                  {editingSlider.image_url && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preview:
                      </label>
                      <img 
                        src={editingSlider.image_url} 
                        alt="Slide preview" 
                        className="w-full h-64 object-cover rounded border"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Buttons */}
              <div className="flex gap-3 pt-6 border-t mt-6">
                <button
                  onClick={() => handleSaveSlider(editingSlider)}
                  disabled={!editingSlider.title || !editingSlider.subtitle}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <i className="ri-save-line mr-2"></i>
                  {editingSlider.id.includes('home-projects-') ? 'Add Slide' : 'Save Changes'}
                </button>
                <button
                  onClick={() => setEditingSlider(null)}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 flex items-center"
                >
                  <i className="ri-close-line mr-2"></i>Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
            <div>
              <h4 className="font-medium text-blue-900 mb-2">About Projects Slider</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• Manages the main project slider section on the homepage</li>
                <li>• Set title, subtitle, image, and button for each slide</li>
                <li>• Slides rotate automatically every 6 seconds</li>
                <li>• Configure slide ordering using the order number</li>
                <li>• Recommended image size: 1920x1080px (16:9 ratio)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLuxuryCollectionModule = () => {
    // Luxury Collection verilerini al
    const luxuryCollections = content.filter(item => 
      item.page === 'home' && 
      item.section === 'luxury-collection' && 
      item.type === 'collection'
    ).sort((a, b) => a.order - b.order);

    const titleContent = getContentItem('home', 'luxury-collection-title');

    const createNewCollection = () => {
      const newCollection: ContentItem = {
        id: `temp-${Date.now()}`,
        page: 'home',
        section: 'luxury-collection',
        type: 'collection',
        content: 'New Collection Title',
        order: luxuryCollections.length,
        metadata: {
          subtitle: 'Subtitle',
          images: []
        }
      };
      setEditingCollection(newCollection);
      setCollectionImages([]);
    };

    const handleSaveCollection = async (item: ContentItem) => {
      try {
        const updatedItem = {
          ...item,
          metadata: {
            ...item.metadata,
            images: collectionImages
          }
        };
        await updateContentItem(updatedItem);
        setEditingCollection(null);
        setCollectionImages([]);
        await reloadContent();
        alert('Collection saved successfully!');
      } catch (error) {
        alert('Failed to save!');
      }
    };

    const handleDeleteCollection = async (id: string) => {
      if (confirm('Are you sure you want to delete this collection?')) {
        try {
          await deleteContentItem(id);
          await reloadContent();
          alert('Collection deleted!');
        } catch (error) {
          alert('Failed to delete!');
        }
      }
    };

    const handleSaveTitle = async () => {
      const titleInput = (document.getElementById('luxury-title-input') as HTMLInputElement)?.value;
      if (!titleInput) return;

      try {
        const titleItem: ContentItem = {
          id: titleContent?.id || `temp-title-${Date.now()}`,
          page: 'home',
          section: 'luxury-collection-title',
          type: 'text',
          content: titleInput,
          order: 0
        };
        await updateContentItem(titleItem);
        await reloadContent();
        alert('Title saved successfully!');
      } catch (error) {
        alert('Failed to save title!');
      }
    };

    return (
      <div className="space-y-6">
        {/* Section Title Edit */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <i className="ri-text mr-2"></i>
            Section Title
          </h3>
          <div className="flex gap-3">
            <input
              id="luxury-title-input"
              type="text"
              defaultValue={titleContent?.content || 'Branded Luxury Collection'}
              placeholder="Enter section title"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
            <button
              onClick={handleSaveTitle}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center whitespace-nowrap"
            >
              <i className="ri-save-line mr-2"></i>
              Kaydet
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            💡 You can use HTML tags. E.g.: &lt;b&gt;Branded Luxury&lt;/b&gt; Collection
          </p>
        </div>

        {/* Collections Listesi */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold flex items-center">
              <i className="ri-gallery-line mr-2"></i>
              Luxury Collections
            </h3>
            <button
              onClick={createNewCollection}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
            >
              <i className="ri-add-line mr-2"></i>Yeni Add Collection
            </button>
          </div>

          {/* Mevcut Collections */}
          <div className="grid gap-4">
            {luxuryCollections.length > 0 ? (
              luxuryCollections.map((collection, index) => {
                const metadata = collection.metadata || {};
                const images = metadata.images || [];
                
                return (
                  <div key={collection.id} className="bg-gray-50 p-6 rounded-lg border">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-medium">
                          Collection {index + 1}
                        </span>
                        <h4 className="font-medium text-lg">{collection.content}</h4>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingCollection(collection);
                            setCollectionImages(images);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center"
                        >
                          <i className="ri-edit-line mr-1"></i>Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCollection(collection.id)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm flex items-center"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>Sil
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Bilgiler */}
                      <div className="space-y-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Title:</label>
                          <p className="text-gray-900 font-medium">{collection.content}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle:</label>
                          <p className="text-gray-600">{metadata.subtitle || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Image Count:</label>
                          <p className="text-gray-600">{images.length} images</p>
                        </div>
                      </div>

                      {/* Images */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Images:</label>
                        <div className="grid grid-cols-4 gap-2">
                          {images.slice(0, 4).map((img: string, idx: number) => (
                            <img 
                              key={idx}
                              src={img} 
                              alt={`${collection.content} ${idx + 1}`}
                              className="w-full h-20 object-cover rounded border"
                            />
                          ))}
                          {images.length === 0 && (
                            <div className="col-span-4 bg-gray-200 h-20 rounded border flex items-center justify-center">
                              <span className="text-gray-500 text-sm">No image</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bg-gray-50 p-8 rounded-lg border-2 border-dashed border-gray-300 text-center">
                <i className="ri-gallery-line text-gray-400 text-4xl mb-4"></i>
                <h4 className="text-lg font-medium text-gray-700 mb-2">No collections added yet</h4>
                <p className="text-gray-500 mb-4">Click the button above to add your first collection.</p>
                <button
                  onClick={createNewCollection}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center mx-auto"
                >
                  <i className="ri-add-line mr-2"></i>Add First Collection
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Edit Collectionme Modal */}
        {editingCollection && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold flex items-center">
                  <i className="ri-edit-line mr-2"></i>
                  {editingCollection.id.includes('temp-') ? 'Yeni Add Collection' : 'Edit Collection'}
                </h3>
                <button
                  onClick={() => {
                    setEditingCollection(null);
                    setCollectionImages([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Title and Subtitle */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={editingCollection.content}
                      onChange={(e) => setEditingCollection({ ...editingCollection, content: e.target.value })}
                      placeholder="e.g. THE SKY MANSION PENTHOUSE"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alt Title *
                    </label>
                    <input
                      type="text"
                      value={editingCollection.metadata?.subtitle || ''}
                      onChange={(e) => setEditingCollection({ 
                        ...editingCollection, 
                        metadata: { ...editingCollection.metadata, subtitle: e.target.value }
                      })}
                      placeholder="e.g. GOLDEN TOWER RESIDENCES"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    />
                  </div>
                </div>

                {/* Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Images (Min 1, Max 10 images)
                  </label>
                  
                  {/* Current Images */}
                  {collectionImages.length > 0 && (
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      {collectionImages.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={img} 
                            alt={`Image ${idx + 1}`}
                            className="w-full h-24 object-cover rounded border"
                          />
                          <button
                            onClick={() => setCollectionImages(collectionImages.filter((_, i) => i !== idx))}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Image */}
                  {collectionImages.length < 10 && (
                    <MediaUploader
                      type="image"
                      currentUrl=""
                      label="Add New Image"
                      onUploadComplete={(url) => setCollectionImages([...collectionImages, url])}
                    />
                  )}
                </div>

                {/* Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order
                  </label>
                  <input
                    type="number"
                    value={editingCollection.order}
                    onChange={(e) => setEditingCollection({ ...editingCollection, order: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  />
                </div>
              </div>

              {/* Alt Butonlar */}
              <div className="flex gap-3 pt-6 border-t mt-6">
                <button
                  onClick={() => handleSaveCollection(editingCollection)}
                  disabled={!editingCollection.content || !editingCollection.metadata?.subtitle || collectionImages.length === 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <i className="ri-save-line mr-2"></i>
                  {editingCollection.id.includes('temp-') ? 'Add Collection' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setEditingCollection(null);
                    setCollectionImages([]);
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 flex items-center"
                >
                  <i className="ri-close-line mr-2"></i>Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bilgi Kutusu */}
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="flex items-start">
            <i className="ri-information-line text-purple-600 text-xl mr-3 mt-0.5"></i>
            <div>
              <h4 className="font-medium text-purple-900 mb-2">About Branded Luxury Collection</h4>
              <ul className="text-purple-800 text-sm space-y-1">
                <li>• You can add title, subtitle and multiple images for each collection</li>
                <li>• Images rotate automatically every 3 seconds</li>
                <li>• Users can navigate between collections with arrow keys</li>
                <li>• Recommended image size: 1600x1000px (16:10 ratio)</li>
                <li>• You can add between 1-10 images per collection</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSliderEditor = (item: SliderItem) => {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h3 className="text-xl font-medium mb-4">
          {item.id.includes('temp-') ? 'Add New Slider' : 'Edit Slider'}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <input
              type="text"
              value={item.title}
              onChange={(e) => setEditingSlider({...item, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
              placeholder="Slider title"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subtitle
            </label>
            <input
              type="text"
              value={item.subtitle}
              onChange={(e) => setEditingSlider({...item, subtitle: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
              placeholder="Slider subtitle"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Order
            </label>
            <input
              type="number"
              value={item.order}
              onChange={(e) => setEditingSlider({...item, order: parseInt(e.target.value) || 0})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
              placeholder="Order number"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Slider Image
            </label>
            {item.image_url && (
              <div className="mb-4">
                <img 
                  src={item.image_url} 
                  alt="Slider Preview" 
                  className="w-full max-h-48 object-cover rounded border"
                />
              </div>
            )}
            <MediaUploader
              type="image"
              currentUrl={item.image_url}
              label="Upload Slider Image"
              onUploadComplete={(url) => setEditingSlider({...item, image_url: url})}
            />
          </div>
        </div>
        
        <div className="flex gap-4 mt-6">
          <button
            onClick={() => handleSaveSlider(item)}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Kaydet
          </button>
          <button
            onClick={() => setEditingSlider(null)}
            className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderSliderEditModal = () => {
    if (!editingSlider) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-medium mb-4 text-gray-900">
            {editingSlider.id.includes('temp-') ? 'Add New Slider' : 'Edit Slider'}
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={slideFormData.title}
                onChange={(e) => setSlideFormData({ ...slideFormData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="e.g. Golden Tower"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subtitle *
              </label>
              <input
                type="text"
                value={slideFormData.subtitle}
                onChange={(e) => setSlideFormData({ ...slideFormData, subtitle: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="e.g. Pinnacle"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <input
                type="text"
                value={slideFormData.description}
                onChange={(e) => setSlideFormData({ ...slideFormData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="e.g. Al Jaddaf"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buton Metni
              </label>
              <input
                type="text"
                value={slideFormData.button_text}
                onChange={(e) => setSlideFormData({ ...slideFormData, button_text: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="e.g. Discover the Collection"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buton URL
              </label>
              <input
                type="text"
                value={slideFormData.button_url}
                onChange={(e) => setSlideFormData({ ...slideFormData, button_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="e.g. /collections"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSaveSlide}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer whitespace-nowrap"
            >
              {editingSlide.id.toString().startsWith('temp-') ? 'Add Slide' : 'Save Changes'}
            </button>
            <button
              onClick={() => {
                setEditingSlide(null);
                setSlideFormData({
                  title: '',
                  subtitle: '',
                  description: '',
                  button_text: '',
                  button_url: '',
                  image: '',
                  bg_image: ''
                });
              }}
              className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPinnacleSliderModule = () => {
    // Pinnacle Slider verilerini al
    const pinnacleSliderContent = content.find(item => 
      item.page === 'home' && 
      item.section === 'pinnacle-slider' && 
      item.type === 'slides'
    );

    const slides = pinnacleSliderContent?.metadata?.slides || [];

    const handleAddSlide = () => {
      setEditingSlide({ id: `temp-${Date.now()}`, order: slides.length });
      setSlideFormData({
        title: '',
        subtitle: '',
        description: '',
        button_text: 'Discover the Collection',
        button_url: '/collections',
        image: '',
        bg_image: ''
      });
    };

    const handleEditSlide = (slide: any) => {
      setEditingSlide(slide);
      setSlideFormData({
        title: slide.title || '',
        subtitle: slide.subtitle || '',
        description: slide.description || '',
        button_text: slide.button_text || 'Discover the Collection',
        button_url: slide.button_url || '/collections',
        image: slide.image || '',
        bg_image: slide.bg_image || ''
      });
    };

    const handleSaveSlide = async () => {
      try {
        const updatedSlides = editingSlide.id.toString().startsWith('temp-')
          ? [...slides, { ...slideFormData, id: Date.now().toString(), order: editingSlide.order }]
          : slides.map((s: any) => s.id === editingSlide.id ? { ...s, ...slideFormData } : s);

        if (pinnacleSliderContent) {
          await updateContentItem({
            ...pinnacleSliderContent,
            metadata: { slides: updatedSlides }
          });
        } else {
          const newItem: ContentItem = {
            id: `temp-${Date.now()}`,
            page: 'home',
            section: 'pinnacle-slider',
            type: 'slides',
            content: '',
            metadata: { slides: updatedSlides },
            order: 0
          };
          await updateContentItem(newItem);
        }

        setEditingSlide(null);
        setSlideFormData({
          title: '',
          subtitle: '',
          description: '',
          button_text: '',
          button_url: '',
          image: '',
          bg_image: ''
        });
        await reloadContent();
        alert('Slide saved successfully!');
      } catch (error) {
        alert('Failed to save!');
      }
    };

    const handleDeleteSlide = async (slideId: string) => {
      if (!confirm('Are you sure you want to delete this slide?')) return;

      try {
        const updatedSlides = slides.filter((s: any) => s.id !== slideId);
        
        if (pinnacleSliderContent) {
          await updateContentItem({
            ...pinnacleSliderContent,
            metadata: { slides: updatedSlides }
          });
        }

        await reloadContent();
        alert('Slide deleted successfully!');
      } catch (error) {
        alert('Failed to delete!');
      }
    };

    const handleMoveSlide = async (slideId: string, direction: 'up' | 'down') => {
      const currentIndex = slides.findIndex((s: any) => s.id === slideId);
      if (
        (direction === 'up' && currentIndex === 0) ||
        (direction === 'down' && currentIndex === slides.length - 1)
      ) return;

      const newSlides = [...slides];
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      [newSlides[currentIndex], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[currentIndex]];
      
      // Order'ları güncelle
      newSlides.forEach((slide, index) => {
        slide.order = index;
      });

      try {
        if (pinnacleSliderContent) {
          await updateContentItem({
            ...pinnacleSliderContent,
            metadata: { slides: newSlides }
          });
        }
        await reloadContent();
      } catch (error) {
        alert('Sorting failed!');
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold">Pinnacle Slider Management</h3>
          <button
            onClick={handleAddSlide}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer whitespace-nowrap flex items-center"
          >
            <i className="ri-add-line mr-2"></i>Yeni Add Slide
          </button>
        </div>

        {/* Slide Listesi */}
        <div className="space-y-4">
          {slides.length > 0 ? (
            slides.map((slide: any, index: number) => (
              <div key={slide.id} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-32 h-20 bg-gray-200 rounded overflow-hidden">
                    {slide.image && (
                      <img src={slide.image} alt={slide.subtitle} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg">{slide.title} {slide.subtitle}</h4>
                    <p className="text-gray-600 text-sm">{slide.description}</p>
                    <p className="text-gray-500 text-xs mt-1">Buton: {slide.button_text}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleMoveSlide(slide.id, 'up')}
                      disabled={index === 0}
                      className="px-2 py-1 text-gray-600 hover:text-gray-800 disabled:opacity-30 cursor-pointer"
                      title="Move Up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => handleMoveSlide(slide.id, 'down')}
                      disabled={index === slides.length - 1}
                      className="px-2 py-1 text-gray-600 hover:text-gray-800 disabled:opacity-30 cursor-pointer"
                      title="Move Down"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => handleEditSlide(slide)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm cursor-pointer whitespace-nowrap"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSlide(slide.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm cursor-pointer whitespace-nowrap"
                    >
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-gray-50 p-8 rounded-lg border-2 border-dashed border-gray-300 text-center">
              <i className="ri-slideshow-line text-gray-400 text-4xl mb-4"></i>
              <h4 className="text-lg font-medium text-gray-700 mb-2">No slides added yet</h4>
              <p className="text-gray-500 mb-4">Click "Add New Slide" button above to add your first slide.</p>
              <button
                onClick={handleAddSlide}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center mx-auto"
              >
                <i className="ri-add-line mr-2"></i>Add First Slide
              </button>
            </div>
          )}
        </div>

        {/* Edit Slideme Formu */}
        {editingSlide && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold flex items-center">
                  <i className="ri-edit-line mr-2"></i>
                  {editingSlide.id.toString().startsWith('temp-') ? 'Yeni Add Slide' : 'Edit Slide'}
                </h3>
                <button
                  onClick={() => {
                    setEditingSlide(null);
                    setSlideFormData({
                      title: '',
                      subtitle: '',
                      description: '',
                      button_text: '',
                      button_url: '',
                      image: '',
                      bg_image: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Sol Taraf - Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={slideFormData.title}
                      onChange={(e) => setSlideFormData({ ...slideFormData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="e.g. Golden Tower"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtitle *
                    </label>
                    <input
                      type="text"
                      value={slideFormData.subtitle}
                      onChange={(e) => setSlideFormData({ ...slideFormData, subtitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="e.g. Pinnacle"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <input
                      type="text"
                      value={slideFormData.description}
                      onChange={(e) => setSlideFormData({ ...slideFormData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="e.g. Al Jaddaf"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Buton Metni
                    </label>
                    <input
                      type="text"
                      value={slideFormData.button_text}
                      onChange={(e) => setSlideFormData({ ...slideFormData, button_text: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="e.g. Discover the Collection"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Buton URL
                    </label>
                    <input
                      type="text"
                      value={slideFormData.button_url}
                      onChange={(e) => setSlideFormData({ ...slideFormData, button_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="e.g. /collections"
                    />
                  </div>
                </div>

                {/* Right Side - Image Upload */}
                <div className="space-y-4">
                  {/* Foreground Image */}
                  <div>
                    <MediaUploader
                      type="image"
                      currentUrl={slideFormData.image}
                      label="Foreground Image (Product image shown on the right)"
                      onUploadComplete={(url) => setSlideFormData({ ...slideFormData, image: url })}
                    />
                  </div>

                  {/* Background Image */}
                  <div>
                    <MediaUploader
                      type="image"
                      currentUrl={slideFormData.bg_image}
                      label="Background Image (Full screen background)"
                      onUploadComplete={(url) => setSlideFormData({ ...slideFormData, bg_image: url })}
                    />
                  </div>
                </div>
              </div>

              {/* Alt Butonlar */}
              <div className="flex gap-3 pt-6 border-t mt-6">
                <button
                  onClick={handleSaveSlide}
                  disabled={!slideFormData.title || !slideFormData.subtitle || !slideFormData.description}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <i className="ri-save-line mr-2"></i>
                  {editingSlide.id.toString().startsWith('temp-') ? 'Add Slide' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setEditingSlide(null);
                    setSlideFormData({
                      title: '',
                      subtitle: '',
                      description: '',
                      button_text: '',
                      button_url: '',
                      image: '',
                      bg_image: ''
                    });
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 flex items-center"
                >
                  <i className="ri-close-line mr-2"></i>Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bilgi Kutusu */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
            <i className="ri-information-line mr-2"></i>
            Usage Information
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• You can enter title, subtitle, description and button information for each slide</li>
            <li>• Foreground image: Product image shown on the right side</li>
            <li>• Background image: Full screen background image</li>
            <li>• You can change slide order using arrow keys</li>
            <li>• Slider rotates automatically every 6 seconds</li>
            <li>• Recommended foreground image size: 800x1000px (portrait)</li>
            <li>• Recommended background image size: 1920x1080px (landscape)</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderAwardsManager = () => {
    // Awards verilerini al
    const awardsContent = content.filter(item => 
      item.page === 'home' && 
      item.section === 'awards'
    );

    // Başlık verisi
    const titleContent = getContentItem('home', 'awards-title');
    
    // Ödül logoları
    const awardLogos = content
      .filter(item => 
        item.page === 'home' && 
        item.section === 'awards' && 
        item.type === 'award_logo'
      )
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const handleAddAward = async () => {
      if (!awardFormData.logo_url.trim()) {
        alert('Please enter a logo URL!');
        return;
      }

      try {
        const newAward: ContentItem = {
          id: `temp-award-${Date.now()}`,
          page: 'home',
          section: 'awards',
          type: 'award_logo',
          content: '',
          image_url: awardFormData.logo_url,
          order: awardLogos.length,
          metadata: {
            logo_url: awardFormData.logo_url
          }
        };

        await updateContentItem(newAward);
        setAwardFormData({ title: '', logo_url: '' });
        setIsAddingAward(false);
        await reloadContent();
        alert('Award added successfully!');
      } catch (error) {
        alert('Failed to add award!');
      }
    };

    const handleUpdateTitle = async () => {
      if (!awardFormData.title.trim()) {
        alert('Please enter a title!');
        return;
      }

      try {
        const titleItem: ContentItem = {
          id: titleContent?.id || `temp-title-${Date.now()}`,
          page: 'home',
          section: 'awards-title',
          type: 'text',
          content: awardFormData.title,
          order: 0
        };

        await updateContentItem(titleItem);
        setAwardFormData({ title: '', logo_url: '' });
        setEditingAwardId(null);
        await reloadContent();
        alert('Title saved successfully!');
      } catch (error) {
        alert('Failed to save title!');
      }
    };

    const handleDeleteAward = async (id: string) => {
      if (window.confirm('Are you sure you want to delete this award?')) {
        try {
          await deleteContentItem(id);
          await reloadContent();
          alert('Award deleted!');
        } catch (error) {
          alert('Failed to delete award!');
        }
      }
    };

    const startEditTitle = () => {
      setAwardFormData({
        title: titleContent?.content || 'AWARDS & RECOGNITIONS',
        logo_url: '',
      });
      setEditingAwardId('title');
    };

    return (
      <div className="space-y-6">
        {/* Title Edit */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Section Title</h3>
            {editingAwardId !== 'title' && (
              <button
                onClick={startEditTitle}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit Title
              </button>
            )}
          </div>

          {editingAwardId === 'title' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={awardFormData.title}
                  onChange={(e) => setAwardFormData({ ...awardFormData, title: e.target.value })}
                  placeholder="e.g. AWARDS & RECOGNITIONS"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateTitle}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingAwardId(null);
                    setAwardFormData({ title: '', logo_url: '' });
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="text-2xl font-light text-gray-900">
              {titleContent?.content || 'AWARDS & RECOGNITIONS'}
            </div>
          )}
        </div>

        {/* Award Logos */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Award Logos ({awardLogos.length})</h3>
            {!isAddingAward && (
              <button
                onClick={() => setIsAddingAward(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                + Add New Award
              </button>
            )}
          </div>

          {isAddingAward && (
            <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Add New Award</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo Image
                  </label>
                  <MediaUploader
                    type="image"
                    currentUrl={awardFormData.logo_url}
                    label="Upload Logo Image"
                    onUploadComplete={(url) => setAwardFormData({ ...awardFormData, logo_url: url })}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleAddAward}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Ekle
                  </button>
                  <button
                    onClick={() => {
                      setIsAddingAward(false);
                      setAwardFormData({ title: '', logo_url: '' });
                    }}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {awardLogos.map((award) => (
              <div
                key={award.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="aspect-video bg-gray-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                  {award.image_url || award.metadata?.logo_url ? (
                    <img
                      src={award.image_url || award.metadata?.logo_url}
                      alt="Award Logo"
                      className="w-full h-full object-contain p-4"
                    />
                  ) : (
                    <i className="ri-award-line text-4xl text-gray-400"></i>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleDeleteAward(award.id)}
                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    Sil
                  </button>
                </div>
              </div>
            ))}
          </div>

          {awardLogos.length === 0 && !isAddingAward && (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <i className="ri-award-line text-5xl text-gray-400 mb-3"></i>
              <p className="text-gray-600">No awards added yet</p>
              <button
                onClick={() => setIsAddingAward(true)}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Add First Award
              </button>
            </div>
          )}
        </div>

        {/* Form Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-file-edit-line mr-2"></i>
              Form Settings
            </h3>
            {editingContactField !== 'form' && (
              <button
                onClick={() => {
                  const formContent = contactFormItem;
                  setContactFormDataForm({
                    form_title: formContent?.metadata?.form_title || 'REQUEST A CALLBACK',
                    submit_button_text: formContent?.metadata?.submit_button_text || 'MEET OUR BRAND AMBASSADOR',
                    default_country_code: formContent?.metadata?.default_country_code || '+224'
                  });
                  setEditingContactField('form');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit Form Settings
              </button>
            )}
          </div>

          {editingContactField === 'form' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Form Title</label>
                <input
                  type="text"
                  value={contactFormDataForm.form_title}
                  onChange={(e) => setContactFormDataForm({ ...contactFormDataForm, form_title: e.target.value })}
                  placeholder="REQUEST A CALLBACK"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Submit Button Text</label>
                <input
                  type="text"
                  value={contactFormDataForm.submit_button_text}
                  onChange={(e) => setContactFormDataForm({ ...contactFormDataForm, submit_button_text: e.target.value })}
                  placeholder="MEET OUR BRAND AMBASSADOR"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Country Code</label>
                <input
                  type="text"
                  value={contactFormDataForm.default_country_code}
                  onChange={(e) => setContactFormDataForm({ ...contactFormDataForm, default_country_code: e.target.value })}
                  placeholder="+224"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    try {
                      const formItem: ContentItem = {
                        id: contactFormItem?.id || `temp-${targetPage}-contact-form-${Date.now()}`,
                        page: targetPage,
                        section: 'contact-form',
                        type: 'form_settings',
                        content: 'Form Settings',
                        order: 0,
                        metadata: {
                          ...(contactFormItem?.metadata || {}),
                          form_title: contactFormDataForm.form_title,
                          submit_button_text: contactFormDataForm.submit_button_text,
                          default_country_code: contactFormDataForm.default_country_code
                        }
                      } as any;
                      await updateContentItem(formItem);
                      setContactFormDataForm({ form_title: '', submit_button_text: '', default_country_code: '' });
                      setEditingContactField('');
                      await reloadContent();
                      alert('Form settings saved successfully!');
                    } catch (error) {
                      alert('Failed to save form settings!');
                    }
                  }}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingContactField(null);
                    setContactFormDataForm({ form_title: '', submit_button_text: '', default_country_code: '' });
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-gray-700">
              <div><span className="font-medium">Form Title:</span> {contactFormItem?.metadata?.form_title || 'REQUEST A CALLBACK'}</div>
              <div><span className="font-medium">Buton Metni:</span> {contactFormItem?.metadata?.submit_button_text || 'MEET OUR BRAND AMBASSADOR'}</div>
              <div><span className="font-medium">Default Country Code:</span> {contactFormItem?.metadata?.default_country_code || '+224'}</div>
            </div>
          )}
        </div>

        {/* Background Image */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-image-line mr-2"></i>
            Background Image (Behind overlay)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div>
              <MediaUploader
                type="image"
                currentUrl={contactBgItem?.image_url || contactBgItem?.metadata?.bg_image_url || ''}
                label="Background Image"
                onUploadComplete={async (url) => {
                  const bgItem: ContentItem = {
                    id: contactBgItem?.id || `temp-${targetPage}-contact-bg-${Date.now()}`,
                    page: targetPage,
                    section: 'contact-bg',
                    type: 'image',
                    content: 'Contact Background',
                    order: 0,
                    image_url: url,
                    metadata: {
                      ...(contactBgItem?.metadata || {}),
                      bg_image_url: url
                    }
                  } as any;
                  await updateContentItem(bgItem);
                  await reloadContent();
                  alert('Background image saved successfully!');
                }}
              />
            </div>
            <div className="text-sm text-gray-600">
              <p>This image is used behind the overlay on amenities-style forms. If left empty, it falls back to Brand → Amenities → Home.</p>
            </div>
          </div>
        </div>

        {/* Bilgi Kutusu */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
            <div>
              <h4 className="font-medium text-blue-900 mb-2">About Awards & Recognitions</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• You can edit the section title</li>
                <li>• You can add and delete award logos</li>
                <li>• Logos are automatically displayed in a grid layout</li>
                <li>• Recommended logo size: 200x100px (2:1 ratio)</li>
                <li>• Logos should be in transparent PNG format</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContactSectionModuleFor = (targetPage: string) => {
    // Contact Section verilerini al
    const contactTitle = getContentItem(targetPage, 'contact-title');
    const contactLinks = getContentItem(targetPage, 'contact-links');
    const contactSocial = getContentItem(targetPage, 'contact-social');
    const contactIcons = getContentItem(targetPage, 'contact-icons');
    const contactFormItem = getContentItem(targetPage, 'contact-form');
    const contactBgItem = getContentItem(targetPage, 'contact-bg') || getContentItem(targetPage, 'footer');

    const handleSaveContactTitle = async () => {
      if (!contactFormData.title.trim()) {
        alert('Please enter a title!');
        return;
      }

      try {
        const titleItem: ContentItem = {
          id: contactTitle?.id || `temp-${targetPage}-contact-title-${Date.now()}`,
          page: targetPage,
          section: 'contact-title',
          type: 'text',
          content: contactFormData.title,
          order: 0
        };

        await updateContentItem(titleItem);
        setContactFormData({ ...contactFormData, title: '' });
        setEditingContactField(null);
        await reloadContent();
        alert('Title saved successfully!');
      } catch (error) {
        alert('Failed to save title!');
      }
    };

    const handleSaveContactLinks = async () => {
      if (!contactFormData.link1_text.trim() || !contactFormData.link2_text.trim()) {
        alert('Please fill in all link information!');
        return;
      }

      try {
        const linksItem: ContentItem = {
          id: contactLinks?.id || `temp-${targetPage}-contact-links-${Date.now()}`,
          page: targetPage,
          section: 'contact-links',
          type: 'links',
          content: '',
          order: 0,
          metadata: {
            link1_text: contactFormData.link1_text,
            link1_url: contactFormData.link1_url,
            link2_text: contactFormData.link2_text,
            link2_url: contactFormData.link2_url
          }
        };

        await updateContentItem(linksItem);
        setContactFormData({ 
          ...contactFormData, 
          link1_text: '', 
          link1_url: '', 
          link2_text: '', 
          link2_url: '' 
        });
        setEditingContactField(null);
        await reloadContent();
        alert('Links saved successfully!');
      } catch (error) {
        alert('Failed to save links!');
      }
    };

    const handleSaveContactSocial = async () => {
      try {
        const socialItem: ContentItem = {
          id: contactSocial?.id || `temp-${targetPage}-contact-social-${Date.now()}`,
          page: targetPage,
          section: 'contact-social',
          type: 'social',
          content: '',
          order: 0,
          metadata: {
            instagram: contactFormData.instagram,
            facebook: contactFormData.facebook,
            youtube: contactFormData.youtube,
            linkedin: contactFormData.linkedin,
            snapchat: contactFormData.snapchat
          }
        };

        await updateContentItem(socialItem);
        setContactFormData({ 
          ...contactFormData, 
          instagram: '', 
          facebook: '', 
          youtube: '', 
          linkedin: '', 
          snapchat: '' 
        });
        setEditingContactField(null);
        await reloadContent();
        alert('Social media accounts saved successfully!');
      } catch (error) {
        alert('Failed to save social media accounts!');
      }
    };

    const handleSaveContactIcons = async () => {
      try {
        const iconsItem: ContentItem = {
          id: contactIcons?.id || `temp-${targetPage}-contact-icons-${Date.now()}`,
          page: targetPage,
          section: 'contact-icons',
          type: 'contact_info',
          content: '',
          order: 0,
          metadata: {
            phone: contactFormData.phone,
            whatsapp: contactFormData.whatsapp,
            email: contactFormData.email,
            pdf_url: contactFormData.pdf_url
          }
        };

        await updateContentItem(iconsItem);
        setContactFormData({ 
          ...contactFormData, 
          phone: '', 
          whatsapp: '', 
          email: '', 
          pdf_url: '' 
        });
        setEditingContactField(null);
        await reloadContent();
        alert('Contact information saved successfully!');
      } catch (error) {
        alert('Failed to save contact information!');
      }
    };

    const startEditTitle = () => {
      setContactFormData({
        ...contactFormData,
        title: contactTitle?.content || 'EXPRESS YOUR INTEREST'
      });
      setEditingContactField('title');
    };

    const startEditLinks = () => {
      const metadata = contactLinks?.metadata || {};
      setContactFormData({
        ...contactFormData,
        link1_text: metadata.link1_text || 'FIND A SALES BOUTIQUE',
        link1_url: metadata.link1_url || '#',
        link2_text: metadata.link2_text || 'DISCOVER THE COLLECTION',
        link2_url: metadata.link2_url || '#'
      });
      setEditingContactField('links');
    };

    const startEditSocial = () => {
      const metadata = contactSocial?.metadata || {};
      setContactFormData({
        ...contactFormData,
        instagram: metadata.instagram || '#',
        facebook: metadata.facebook || '#',
        youtube: metadata.youtube || '#',
        linkedin: metadata.linkedin || '#',
        snapchat: metadata.snapchat || '#'
      });
      setEditingContactField('social');
    };

    const startEditIcons = () => {
      const metadata = contactIcons?.metadata || {};
      setContactFormData({
        ...contactFormData,
        phone: metadata.phone || '+971123456789',
        whatsapp: metadata.whatsapp || '971123456789',
        email: metadata.email || 'info@goldentower.com',
        pdf_url: metadata.pdf_url || '/catalog.pdf'
      });
      setEditingContactField('icons');
    };

    return (
      <div className="space-y-6">
        {/* Title Edit */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-text mr-2"></i>
              Section Title
            </h3>
            {editingContactField !== 'title' && (
              <button
                onClick={startEditTitle}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit Title
              </button>
            )}
          </div>

          {editingContactField === 'title' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title (Each line on a new line)
                </label>
                <textarea
                  value={contactFormData.title}
                  onChange={(e) => setContactFormData({ ...contactFormData, title: e.target.value })}
                  placeholder="EXPRESS&#10;YOUR&#10;INTEREST"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                />
                <p className="text-sm text-gray-500 mt-1">
                  💡 Enter each line on a new line. E.g.: EXPRESS (Enter) YOUR (Enter) INTEREST
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSaveContactTitle}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingContactField(null);
                    setContactFormData({ ...contactFormData, title: '' });
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="text-2xl font-light text-gray-900 whitespace-pre-line">
              {contactTitle?.content || 'EXPRESS\nYOUR\nINTEREST'}
            </div>
          )}
        </div>

        {/* Links Edit */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-links-line mr-2"></i>
              Linkler
            </h3>
            {editingContactField !== 'links' && (
              <button
                onClick={startEditLinks}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Linkleri Edit
              </button>
            )}
          </div>

          {editingContactField === 'links' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link 1 Metni
                  </label>
                  <input
                    type="text"
                    value={contactFormData.link1_text}
                    onChange={(e) => setContactFormData({ ...contactFormData, link1_text: e.target.value })}
                    placeholder="FIND A SALES BOUTIQUE"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link 1 URL
                  </label>
                  <input
                    type="text"
                    value={contactFormData.link1_url}
                    onChange={(e) => setContactFormData({ ...contactFormData, link1_url: e.target.value })}
                    placeholder="#"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link 2 Metni
                  </label>
                  <input
                    type="text"
                    value={contactFormData.link2_text}
                    onChange={(e) => setContactFormData({ ...contactFormData, link2_text: e.target.value })}
                    placeholder="DISCOVER THE COLLECTION"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link 2 URL
                  </label>
                  <input
                    type="text"
                    value={contactFormData.link2_url}
                    onChange={(e) => setContactFormData({ ...contactFormData, link2_url: e.target.value })}
                    placeholder="#"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSaveContactLinks}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingContactField(null);
                    setContactFormData({ 
                      ...contactFormData, 
                      link1_text: '', 
                      link1_url: '', 
                      link2_text: '', 
                      link2_url: '' 
                    });
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center text-gray-700">
                <i className="ri-arrow-right-line mr-2 text-[#C5A46D]"></i>
                <span>{contactLinks?.metadata?.link1_text || 'FIND A SALES BOUTIQUE'}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-arrow-right-line mr-2 text-[#C5A46D]"></i>
                <span>{contactLinks?.metadata?.link2_text || 'DISCOVER THE COLLECTION'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Social Media Edit */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-share-line mr-2"></i>
              Social Media Accounts
            </h3>
            {editingContactField !== 'social' && (
              <button
                onClick={startEditSocial}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit Social Media
              </button>
            )}
          </div>

          {editingContactField === 'social' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-instagram-line mr-2"></i>Instagram URL
                  </label>
                  <input
                    type="text"
                    value={contactFormData.instagram}
                    onChange={(e) => setContactFormData({ ...contactFormData, instagram: e.target.value })}
                    placeholder="https://instagram.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-facebook-fill mr-2"></i>Facebook URL
                  </label>
                  <input
                    type="text"
                    value={contactFormData.facebook}
                    onChange={(e) => setContactFormData({ ...contactFormData, facebook: e.target.value })}
                    placeholder="https://facebook.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-youtube-fill mr-2"></i>YouTube URL
                  </label>
                  <input
                    type="text"
                    value={contactFormData.youtube}
                    onChange={(e) => setContactFormData({ ...contactFormData, youtube: e.target.value })}
                    placeholder="https://youtube.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-linkedin-fill mr-2"></i>LinkedIn URL
                  </label>
                  <input
                    type="text"
                    value={contactFormData.linkedin}
                    onChange={(e) => setContactFormData({ ...contactFormData, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-twitter-fill mr-2"></i>X (Twitter) URL
                  </label>
                  <input
                    type="text"
                    value={contactFormData.snapchat}
                    onChange={(e) => setContactFormData({ ...contactFormData, snapchat: e.target.value })}
                    placeholder="https://x.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSaveContactSocial}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingContactField(null);
                    setContactFormData({ 
                      ...contactFormData, 
                      instagram: '', 
                      facebook: '', 
                      youtube: '', 
                      linkedin: '', 
                      snapchat: '' 
                    });
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-4">
              <a href={contactSocial?.metadata?.instagram || '#'} className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a href={contactSocial?.metadata?.facebook || '#'} className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a href={contactSocial?.metadata?.youtube || '#'} className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                <i className="ri-youtube-fill text-lg"></i>
              </a>
              <a href={contactSocial?.metadata?.linkedin || '#'} className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
              <a href={contactSocial?.metadata?.snapchat || '#'} className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                <i className="ri-twitter-fill text-lg"></i>
              </a>
            </div>
          )}
        </div>

        {/* Contact Icons Edit */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-customer-service-2-line mr-2"></i>
              Right Side Contact Icons
            </h3>
            {editingContactField !== 'icons' && (
              <button
                onClick={startEditIcons}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit Contact Information
              </button>
            )}
          </div>

          {editingContactField === 'icons' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-phone-fill mr-2"></i>Phone Number
                  </label>
                  <input
                    type="text"
                    value={contactFormData.phone}
                    onChange={(e) => setContactFormData({ ...contactFormData, phone: e.target.value })}
                    placeholder="+971123456789"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-whatsapp-fill mr-2"></i>WhatsApp Number
                  </label>
                  <input
                    type="text"
                    value={contactFormData.whatsapp}
                    onChange={(e) => setContactFormData({ ...contactFormData, whatsapp: e.target.value })}
                    placeholder="971123456789"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    💡 Include country code, without the + sign
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-mail-fill mr-2"></i>Email Adresi
                  </label>
                  <input
                    type="email"
                    value={contactFormData.email}
                    onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                    placeholder="info@goldentower.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-file-pdf-fill mr-2"></i>PDF Katalog URL
                  </label>
                  <input
                    type="text"
                    value={contactFormData.pdf_url}
                    onChange={(e) => setContactFormData({ ...contactFormData, pdf_url: e.target.value })}
                    placeholder="/catalog.pdf"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSaveContactIcons}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingContactField(null);
                    setContactFormData({ 
                      ...contactFormData, 
                      phone: '', 
                      whatsapp: '', 
                      email: '', 
                      pdf_url: '' 
                    });
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center text-gray-700">
                <i className="ri-phone-fill mr-3 text-[#C5A46D]"></i>
                <span>{contactIcons?.metadata?.phone || '+971123456789'}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-whatsapp-fill mr-3 text-[#25D366]"></i>
                <span>{contactIcons?.metadata?.whatsapp || '971123456789'}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-mail-fill mr-3 text-[#C5A46D]"></i>
                <span>{contactIcons?.metadata?.email || 'info@goldentower.com'}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-file-pdf-fill mr-3 text-red-600"></i>
                <span>{contactIcons?.metadata?.pdf_url || '/catalog.pdf'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Bilgi Kutusu */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
            <div>
              <h4 className="font-medium text-blue-900 mb-2">About Contact Section</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• Title: You can change the title of the contact section</li>
                <li>• Links: You can configure two link texts and URLs</li>
                <li>• Social Media: You can add links to all your social media accounts</li>
                <li>• Contact Icons: You can set phone, WhatsApp, email and PDF catalog info shown on the right</li>
                <li>• You can configure the form title, button text and default country code</li>
                <li>• You can upload a background image to match the amenities-style overlay look</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContactSectionManager = () => {
    return (
      <div className="space-y-8">
        {/* Title Management */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Section Title</h3>
          <div className="space-y-4">
            {editingContactTitle ? (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Title (Each line on a new line)
                </label>
                <textarea
                  value={contactTitleForm}
                  onChange={(e) => setContactTitleForm(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                  placeholder="EXPRESS&#10;YOUR&#10;INTEREST"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSaveContactTitle()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Kaydet
                  </button>
                  <button
                    onClick={() => {
                      setEditingContactTitle(false);
                      setContactTitleForm(getContentItem('home', 'contact-title')?.content || 'EXPRESS\nYOUR\nINTEREST');
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">Section Title</p>
                  <p className="text-gray-600 whitespace-pre-line">
                    {getContentItem('home', 'contact-title')?.content || 'EXPRESS\nYOUR\nINTEREST'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingContactTitle(true);
                    setContactTitleForm(getContentItem('home', 'contact-title')?.content || 'EXPRESS\nYOUR\nINTEREST');
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Form Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-file-edit-line mr-2"></i>
              Form Settings
            </h3>
            {editingContactField !== 'form' && (
              <button
                onClick={() => {
                  const formContent = getContentItem('home', 'contact-form');
                  setContactFormDataForm({
                    form_title: formContent?.metadata?.form_title || 'REQUEST A CALLBACK',
                    submit_button_text: formContent?.metadata?.submit_button_text || 'MEET OUR BRAND AMBASSADOR',
                    default_country_code: formContent?.metadata?.default_country_code || '+224'
                  });
                  setEditingContactField('form');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit Form Settings
              </button>
            )}
          </div>

          {editingContactField === 'form' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Form Title
                </label>
                <input
                  type="text"
                  value={contactFormDataForm.form_title}
                  onChange={(e) => setContactFormDataForm({ ...contactFormDataForm, form_title: e.target.value })}
                  placeholder="REQUEST A CALLBACK"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Submit Butonu Metni
                </label>
                <input
                  type="text"
                  value={contactFormDataForm.submit_button_text}
                  onChange={(e) => setContactFormDataForm({ ...contactFormDataForm, submit_button_text: e.target.value })}
                  placeholder="MEET OUR BRAND AMBASSADOR"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Country Code
                </label>
                <select
                  value={contactFormDataForm.default_country_code}
                  onChange={(e) => setContactFormDataForm({ ...contactFormDataForm, default_country_code: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white pr-8"
                >
                  <option value="+224">+224</option>
                  <option value="+971">+971</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+90">+90</option>
                  <option value="+966">+966</option>
                  <option value="+974">+974</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    try {
                      const formItem: ContentItem = {
                        id: getContentItem('home', 'contact-form')?.id || `temp-contact-form-${Date.now()}`,
                        page: 'home',
                        section: 'contact-form',
                        type: 'form_settings',
                        content: 'Form Settings',
                        order: 0,
                        metadata: contactFormDataForm
                      };

                      await updateContentItem(formItem);
                      setEditingContactField(null);
                      await reloadContent();
                      alert('Form settings saved successfully!');
                    } catch (error) {
                      alert('Failed to save form settings!');
                    }
                  }}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingContactField(null);
                    setContactFormDataForm({ form_title: '', submit_button_text: '', default_country_code: '' });
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-gray-700">
              <div>
                <span className="font-medium">Form Title:</span> {getContentItem('home', 'contact-form')?.metadata?.form_title || 'REQUEST A CALLBACK'}
              </div>
              <div>
                <span className="font-medium">Buton Metni:</span> {getContentItem('home', 'contact-form')?.metadata?.submit_button_text || 'MEET OUR BRAND AMBASSADOR'}
              </div>
              <div>
                <span className="font-medium">Default Country Code:</span> {getContentItem('home', 'contact-form')?.metadata?.default_country_code || '+224'}
              </div>
            </div>
          )}
        </div>

        {/* Links Management */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Left Side Links</h3>
          <div className="space-y-4">
            {editingContactLinks ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Link Text
                    </label>
                    <input
                      type="text"
                      value={contactLinksData.link1_text}
                      onChange={(e) => setContactLinksData({...contactLinksData, link1_text: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="FIND A SALES BOUTIQUE"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Link URL
                    </label>
                    <input
                      type="text"
                      value={contactLinksData.link1_url}
                      onChange={(e) => setContactLinksData({...contactLinksData, link1_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Second Link Text
                    </label>
                    <input
                      type="text"
                      value={contactLinksData.link2_text}
                      onChange={(e) => setContactLinksData({...contactLinksData, link2_text: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="DISCOVER THE COLLECTION"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Second Link URL
                    </label>
                    <input
                      type="text"
                      value={contactLinksData.link2_url}
                      onChange={(e) => setContactLinksData({...contactLinksData, link2_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSaveContactLinks()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Kaydet
                  </button>
                  <button
                    onClick={() => {
                      setEditingContactLinks(false);
                      const linksContent = getContentItem('home', 'contact-links');
                      setContactLinksData({
                        link1_text: linksContent?.metadata?.link1_text || 'FIND A SALES BOUTIQUE',
                        link1_url: linksContent?.metadata?.link1_url || '#',
                        link2_text: linksContent?.metadata?.link2_text || 'DISCOVER THE COLLECTION',
                        link2_url: linksContent?.metadata?.link2_url || '#'
                      });
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">Left Side Links</p>
                  <p className="text-sm text-gray-600">
                    {getContentItem('home', 'contact-links')?.metadata?.link1_text || 'FIND A SALES BOUTIQUE'} → {getContentItem('home', 'contact-links')?.metadata?.link1_url || '#'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {getContentItem('home', 'contact-links')?.metadata?.link2_text || 'DISCOVER THE COLLECTION'} → {getContentItem('home', 'contact-links')?.metadata?.link2_url || '#'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingContactLinks(true);
                    const linksContent = getContentItem('home', 'contact-links');
                    setContactLinksData({
                      link1_text: linksContent?.metadata?.link1_text || 'FIND A SALES BOUTIQUE',
                      link1_url: linksContent?.metadata?.link1_url || '#',
                      link2_text: linksContent?.metadata?.link2_text || 'DISCOVER THE COLLECTION',
                      link2_url: linksContent?.metadata?.link2_url || '#'
                    });
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Social Media Management */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Social Media Accounts</h3>
          <div className="space-y-4">
            {editingContactSocial ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instagram
                    </label>
                    <input
                      type="text"
                      value={contactSocialData.instagram}
                      onChange={(e) => setContactSocialData({...contactSocialData, instagram: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facebook
                    </label>
                    <input
                      type="text"
                      value={contactSocialData.facebook}
                      onChange={(e) => setContactSocialData({...contactSocialData, facebook: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      YouTube
                    </label>
                    <input
                      type="text"
                      value={contactSocialData.youtube}
                      onChange={(e) => setContactSocialData({...contactSocialData, youtube: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      LinkedIn
                    </label>
                    <input
                      type="text"
                      value={contactSocialData.linkedin}
                      onChange={(e) => setContactSocialData({...contactSocialData, linkedin: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://linkedin.com/..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    X (Twitter)
                  </label>
                  <input
                    type="text"
                    value={contactSocialData.snapchat}
                    onChange={(e) => setContactSocialData({...contactSocialData, snapchat: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://x.com/..."
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSaveContactSocial()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Kaydet
                  </button>
                  <button
                    onClick={() => {
                      setEditingContactSocial(false);
                      const socialContent = getContentItem('home', 'contact-social');
                      setContactSocialData({
                        instagram: socialContent?.metadata?.instagram || '#',
                        facebook: socialContent?.metadata?.facebook || '#',
                        youtube: socialContent?.metadata?.youtube || '#',
                        linkedin: socialContent?.metadata?.linkedin || '#',
                        snapchat: socialContent?.metadata?.snapchat || '#'
                      });
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">Social Media Accounts</p>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mt-2">
                    <p>Instagram: {getContentItem('home', 'contact-social')?.metadata?.instagram || '#'}</p>
                    <p>Facebook: {getContentItem('home', 'contact-social')?.metadata?.facebook || '#'}</p>
                    <p>YouTube: {getContentItem('home', 'contact-social')?.metadata?.youtube || '#'}</p>
                    <p>LinkedIn: {getContentItem('home', 'contact-social')?.metadata?.linkedin || '#'}</p>
                    <p>X (Twitter): {getContentItem('home', 'contact-social')?.metadata?.snapchat || '#'}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setEditingContactSocial(true);
                    const socialContent = getContentItem('home', 'contact-social');
                    setContactSocialData({
                      instagram: socialContent?.metadata?.instagram || '#',
                      facebook: socialContent?.metadata?.facebook || '#',
                      youtube: socialContent?.metadata?.youtube || '#',
                      linkedin: socialContent?.metadata?.linkedin || '#',
                      snapchat: socialContent?.metadata?.snapchat || '#'
                    });
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Contact Icons Management */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Right Side Contact Icons</h3>
          <div className="space-y-4">
            {editingContactIcons ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      value={contactIconsData.phone}
                      onChange={(e) => setContactIconsData({...contactIconsData, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="+971123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WhatsApp Number (without country code)
                    </label>
                    <input
                      type="text"
                      value={contactIconsData.whatsapp}
                      onChange={(e) => setContactIconsData({...contactIconsData, whatsapp: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="971123456789"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Adresi
                    </label>
                    <input
                      type="email"
                      value={contactIconsData.email}
                      onChange={(e) => setContactIconsData({...contactIconsData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="info@goldentower.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PDF Katalog URL'si
                    </label>
                    <input
                      type="text"
                      value={contactIconsData.pdf_url}
                      onChange={(e) => setContactIconsData({...contactIconsData, pdf_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                      placeholder="/catalog.pdf"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSaveContactIcons()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Kaydet
                  </button>
                  <button
                    onClick={() => {
                      setEditingContactIcons(false);
                      const iconsContent = getContentItem('home', 'contact-icons');
                      setContactIconsData({
                        phone: iconsContent?.metadata?.phone || '+971123456789',
                        whatsapp: iconsContent?.metadata?.whatsapp || '971123456789',
                        email: iconsContent?.metadata?.email || 'info@goldentower.com',
                        pdf_url: iconsContent?.metadata?.pdf_url || '/catalog.pdf'
                      });
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">Contact Icons</p>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mt-2">
                    <p>Telefon: {getContentItem('home', 'contact-icons')?.metadata?.phone || '+971123456789'}</p>
                    <p>WhatsApp: {getContentItem('home', 'contact-icons')?.metadata?.whatsapp || '971123456789'}</p>
                    <p>Email: {getContentItem('home', 'contact-icons')?.metadata?.email || 'info@goldentower.com'}</p>
                    <p>PDF: {getContentItem('home', 'contact-icons')?.metadata?.pdf_url || '/catalog.pdf'}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setEditingContactIcons(true);
                    const iconsContent = getContentItem('home', 'contact-icons');
                    setContactIconsData({
                      phone: iconsContent?.metadata?.phone || '+971123456789',
                      whatsapp: iconsContent?.metadata?.whatsapp || '971123456789',
                      email: iconsContent?.metadata?.email || 'info@goldentower.com',
                      pdf_url: iconsContent?.metadata?.pdf_url || '/catalog.pdf'
                    });
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContactPageManager = () => {
    const renderContactHeroSection = () => {
      const heroTitle = getContentItem('contact', 'hero-title');
      const heroSubtitle = getContentItem('contact', 'hero-subtitle');
      const heroBgImage = getContentItem('contact', 'hero-bg-image');
      const heroVideo = getContentItem('contact', 'hero-video');
      const heroPoster = getContentItem('contact', 'hero-poster');
      const formBgImage = getContentItem('contact', 'form-bg-image');

      const handleSaveHeroTitle = async () => {
        try {
          const titleItem: ContentItem = {
            id: heroTitle?.id || `temp-contact-hero-title-${Date.now()}`,
            page: 'contact',
            section: 'hero-title',
            type: 'text',
            content: heroTitleForm,
            order: 0
          };
          await updateContentItem(titleItem);
          setEditingHeroTitle(false);
          await reloadContent();
          alert('Title saved successfully!');
        } catch (error) {
          alert('Failed to save title!');
        }
      };

      const handleSaveHeroSubtitle = async () => {
        try {
          const subtitleItem: ContentItem = {
            id: heroSubtitle?.id || `temp-contact-hero-subtitle-${Date.now()}`,
            page: 'contact',
            section: 'hero-subtitle',
            type: 'text',
            content: heroSubtitleForm,
            order: 0
          };
          await updateContentItem(subtitleItem);
          setEditingHeroSubtitle(false);
          await reloadContent();
          alert('Subtitle saved successfully!');
        } catch (error) {
          alert('Failed to save subtitle!');
        }
      };

      const handleSaveHeroBgImage = async (url: string) => {
        try {
          const bgImageItem: ContentItem = {
            id: heroBgImage?.id || `temp-contact-hero-bg-${Date.now()}`,
            page: 'contact',
            section: 'hero-bg-image',
            type: 'image',
            content: url,
            image_url: url,
            order: 0
          };
          await updateContentItem(bgImageItem);
          await reloadContent();
          alert('Background image saved successfully!');
        } catch (error) {
          alert('Failed to save background image!');
        }
      };

      const handleDeleteHeroBgImage = async () => {
        if (!heroBgImage?.id) return;
        if (!confirm('Do you want to remove the hero background image?')) return;
        try {
          await deleteContentItem(heroBgImage.id);
          await reloadContent();
          alert('Hero background image removed');
        } catch (error) {
          alert('Failed to remove hero background image');
        }
      };

      const handleSaveHeroVideo = async (url: string) => {
        try {
          const videoItem: ContentItem = {
            id: heroVideo?.id || `temp-contact-hero-video-${Date.now()}`,
            page: 'contact',
            section: 'hero-video',
            type: 'video',
            content: url,
            video_url: url,
            order: 0
          };
          await updateContentItem(videoItem);
          await reloadContent();
          alert('Hero video saved successfully!');
        } catch (error) {
          alert('Failed to save hero video!');
        }
      };

      const handleSaveHeroPoster = async (url: string) => {
        try {
          const posterItem: ContentItem = {
            id: heroPoster?.id || `temp-contact-hero-poster-${Date.now()}`,
            page: 'contact',
            section: 'hero-poster',
            type: 'image',
            content: url,
            image_url: url,
            order: 0
          };
          await updateContentItem(posterItem);
          await reloadContent();
          alert('Hero poster saved successfully!');
        } catch (error) {
          alert('Failed to save hero poster!');
        }
      };

      const handleSaveFormBgImage = async (url: string) => {
        try {
          const item: ContentItem = {
            id: formBgImage?.id || `temp-contact-form-bg-${Date.now()}`,
            page: 'contact',
            section: 'form-bg-image',
            type: 'image',
            content: url,
            image_url: url,
            order: 0
          };
          await updateContentItem(item);
          await reloadContent();
          alert('Form background image saved successfully!');
        } catch (error) {
          alert('Failed to save form background image!');
        }
      };

      return (
        <div className="space-y-6">
          {/* Hero Title */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className="ri-text mr-2"></i>
                Hero Title
              </h3>
              {!editingHeroTitle && (
                <button
                  onClick={() => {
                    setEditingHeroTitle(true);
                    setHeroTitleForm(heroTitle?.content || 'GET IN\nTOUCH');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Edit
                </button>
              )}
            </div>

            {editingHeroTitle ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title (Each line on a new line)
                  </label>
                  <textarea
                    value={heroTitleForm}
                    onChange={(e) => setHeroTitleForm(e.target.value)}
                    placeholder="GET IN&#10;TOUCH"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    💡 Enter each line on a new line. E.g.: GET IN (Enter) TOUCH
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveHeroTitle}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Kaydet
                  </button>
                  <button
                    onClick={() => setEditingHeroTitle(false)}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-2xl font-light text-gray-900 whitespace-pre-line">
                {heroTitle?.content || 'GET IN\nTOUCH'}
              </div>
            )}
          </div>

          {/* Hero Subtitle */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className="ri-text mr-2"></i>
                Hero Subtitle
              </h3>
              {!editingHeroSubtitle && (
                <button
                  onClick={() => {
                    setEditingHeroSubtitle(true);
                    setHeroSubtitleForm(heroSubtitle?.content || 'Ready to discover your dream home? Our expert team is here to guide you through every step of your luxury living journey.');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Edit
                </button>
              )}
            </div>

            {editingHeroSubtitle ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subtitle
                  </label>
                  <textarea
                    value={heroSubtitleForm}
                    onChange={(e) => setHeroSubtitleForm(e.target.value)}
                    placeholder="Ready to discover your dream home?"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveHeroSubtitle}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Kaydet
                  </button>
                  <button
                    onClick={() => setEditingHeroSubtitle(false)}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-gray-700">
                {heroSubtitle?.content || 'Ready to discover your dream home? Our expert team is here to guide you through every step of your luxury living journey.'}
              </div>
            )}
          </div>

          {/* Hero Background Image */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
              <i className="ri-image-line mr-2"></i>
              Hero Background Image
            </h3>
            <MediaUploader
              type="image"
              currentUrl={heroBgImage?.image_url || heroBgImage?.content || ''}
              label="Background Image"
              onUploadComplete={handleSaveHeroBgImage}
            />
            {(heroBgImage?.image_url || heroBgImage?.content) && (
              <div className="mt-4 flex items-start gap-4">
                <img 
                  src={heroBgImage?.image_url || heroBgImage?.content} 
                  alt="Hero Background" 
                  className="w-full h-48 object-cover rounded border"
                />
                <button
                  onClick={handleDeleteHeroBgImage}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 cursor-pointer whitespace-nowrap"
                >
                  Remove Image
                </button>
              </div>
            )}
          </div>

          {/* Hero Video ve Poster */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
              <i className="ri-film-line mr-2"></i>
              Hero Video / Poster
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MediaUploader
                type="video"
                currentUrl={heroVideo?.video_url || heroVideo?.content || ''}
                label="Hero Video"
                onUploadComplete={handleSaveHeroVideo}
              />
              <MediaUploader
                type="image"
                currentUrl={heroPoster?.image_url || heroPoster?.content || ''}
                label="Video Poster Image"
                onUploadComplete={handleSaveHeroPoster}
              />
            </div>
          </div>

          {/* Form Background Image */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
              <i className="ri-layout-row-line mr-2"></i>
              Form Background Image
            </h3>
            <MediaUploader
              type="image"
              currentUrl={formBgImage?.image_url || formBgImage?.content || ''}
              label="Form Background Image"
              onUploadComplete={handleSaveFormBgImage}
            />
            {(formBgImage?.image_url || formBgImage?.content) && (
              <div className="mt-4">
                <img
                  src={formBgImage?.image_url || formBgImage?.content}
                  alt="Form Background"
                  className="w-full h-40 object-cover rounded border"
                />
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderContactInfoModule = () => {
      const contactInfoItems = content.filter(item => 
        item.page === 'contact' && 
        item.section === 'contact-info'
      ).sort((a, b) => (a.order || 0) - (b.order || 0));

      const handleAddInfo = () => {
        setEditingInfo({
          id: `temp-contact-info-${Date.now()}`,
          page: 'contact',
          section: 'contact-info',
          type: 'contact_item',
          content: '',
          order: contactInfoItems.length,
          metadata: { title: '', value: '', icon: 'ri-phone-line' }
        });
        setInfoFormData({ title: '', value: '', icon: 'ri-phone-line' });
      };

      const handleSaveInfo = async () => {
        if (!infoFormData.title || !infoFormData.value) {
          alert('Please fill in all fields!');
          return;
        }

        try {
          const infoItem: ContentItem = {
            ...editingInfo!,
            content: infoFormData.title,
            metadata: {
              title: infoFormData.title,
              value: infoFormData.value,
              icon: infoFormData.icon
            }
          };
          await updateContentItem(infoItem);
          setEditingInfo(null);
          await reloadContent();
          alert('Contact info saved successfully!');
        } catch (error) {
          alert('Failed to save!');
        }
      };

      const handleDeleteInfo = async (id: string) => {
        if (confirm('Are you sure you want to delete this contact info?')) {
          try {
            await deleteContentItem(id);
            await reloadContent();
            alert('Contact info deleted!');
          } catch (error) {
            alert('Failed to delete!');
          }
        }
      };

      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Contact Information</h3>
            <button
              onClick={handleAddInfo}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
            >
              <i className="ri-add-line mr-2"></i>Yeni Bilgi Ekle
            </button>
          </div>

          <div className="grid gap-4">
            {contactInfoItems.map((item) => (
              <div key={item.id} className="bg-white p-6 rounded-lg border shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full border border-[#C5A46D] flex items-center justify-center">
                      <i className={`${item.metadata?.icon || 'ri-phone-line'} text-[#C5A46D] text-xl`}></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">{item.metadata?.title}</h4>
                      <p className="text-gray-600">{item.metadata?.value}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingInfo(item);
                        setInfoFormData({
                          title: item.metadata?.title || '',
                          value: item.metadata?.value || '',
                          icon: item.metadata?.icon || 'ri-phone-line'
                        });
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm cursor-pointer whitespace-nowrap"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteInfo(item.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm cursor-pointer whitespace-nowrap"
                    >
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {editingInfo && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="text-xl font-semibold mb-4">
                  {editingInfo.id.includes('temp-') ? 'New Contact Information' : 'Edit Contact Information'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={infoFormData.title}
                      onChange={(e) => setInfoFormData({ ...infoFormData, title: e.target.value })}
                      placeholder="e.g. Sales Hotline"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Value</label>
                    <input
                      type="text"
                      value={infoFormData.value}
                      onChange={(e) => setInfoFormData({ ...infoFormData, value: e.target.value })}
                      placeholder="e.g. +971 4 123 4567"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                    <select
                      value={infoFormData.icon}
                      onChange={(e) => setInfoFormData({ ...infoFormData, icon: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white pr-8"
                    >
                      <option value="ri-phone-line">Telefon</option>
                      <option value="ri-mail-line">Email</option>
                      <option value="ri-map-pin-line">Adres</option>
                      <option value="ri-time-line">Working Hours</option>
                      <option value="ri-whatsapp-line">WhatsApp</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSaveInfo}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer whitespace-nowrap"
                  >
                    Kaydet
                  </button>
                  <button
                    onClick={() => setEditingInfo(null)}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };

    const renderQuickActionsModule = () => {
      const quickActions = getContentItem('contact', 'quick-actions');

      const handleSave = async () => {
        try {
          const item: ContentItem = {
            id: quickActions?.id || `temp-quick-actions-${Date.now()}`,
            page: 'contact',
            section: 'quick-actions',
            type: 'quick_actions',
            content: 'Quick Actions',
            order: 0,
            metadata: quickActionsFormData
          };
          await updateContentItem(item);
          setEditingQuickActions(false);
          await reloadContent();
          alert('Quick actions saved successfully!');
        } catch (error) {
          alert('Failed to save!');
        }
      };

      return (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-flashlight-line mr-2"></i>
              Quick Actions
            </h3>
            {!editingQuickActions && (
              <button
                onClick={() => {
                  const qa = getContentItem('contact', 'quick-actions');
                  setQuickActionsFormData({
                    call_text: qa?.metadata?.call_text || 'Call Now',
                    call_url: qa?.metadata?.call_url || 'tel:+97141234567',
                    whatsapp_text: qa?.metadata?.whatsapp_text || 'WhatsApp',
                    whatsapp_url: qa?.metadata?.whatsapp_url || 'https://wa.me/97141234567'
                  });
                  setEditingQuickActions(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit
              </button>
            )}
          </div>

          {editingQuickActions ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Call Now Buton Metni
                  </label>
                  <input
                    type="text"
                    value={quickActionsFormData.call_text}
                    onChange={(e) => setQuickActionsFormData({ ...quickActionsFormData, call_text: e.target.value })}
                    placeholder="Call Now"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Call Now URL
                  </label>
                  <input
                    type="text"
                    value={quickActionsFormData.call_url}
                    onChange={(e) => setQuickActionsFormData({ ...quickActionsFormData, call_url: e.target.value })}
                    placeholder="tel:+97141234567"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Buton Metni
                  </label>
                  <input
                    type="text"
                    value={quickActionsFormData.whatsapp_text}
                    onChange={(e) => setQuickActionsFormData({ ...quickActionsFormData, whatsapp_text: e.target.value })}
                    placeholder="WhatsApp"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp URL
                  </label>
                  <input
                    type="text"
                    value={quickActionsFormData.whatsapp_url}
                    onChange={(e) => setQuickActionsFormData({ ...quickActionsFormData, whatsapp_url: e.target.value })}
                    placeholder="https://wa.me/97141234567"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => setEditingQuickActions(false)}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Call Now Butonu</p>
                  <p className="text-sm text-gray-600">{quickActionsFormData.call_text} → {quickActionsFormData.call_url}</p>
                </div>
                <i className="ri-phone-fill text-2xl text-[#C5A46D]"></i>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">WhatsApp Butonu</p>
                  <p className="text-sm text-gray-600">{quickActionsFormData.whatsapp_text} → {quickActionsFormData.whatsapp_url}</p>
                </div>
                <i className="ri-whatsapp-fill text-2xl text-[#25D366]"></i>
              </div>
            </div>
          )}
        </div>
      );
    };

    const renderSocialMediaModule = () => {
      const socialMedia = getContentItem('contact', 'social-media');

      const handleSave = async () => {
        try {
          const item: ContentItem = {
            id: socialMedia?.id || `temp-social-media-${Date.now()}`,
            page: 'contact',
            section: 'social-media',
            type: 'social_media',
            content: 'Social Media',
            order: 0,
            metadata: socialMediaFormData
          };
          await updateContentItem(item);
          setEditingSocialMedia(false);
          await reloadContent();
          alert('Social media accounts saved successfully!');
        } catch (error) {
          alert('Failed to save!');
        }
      };

      return (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-share-line mr-2"></i>
              Social Media Accounts
            </h3>
            {!editingSocialMedia && (
              <button
                onClick={() => {
                  const sm = getContentItem('contact', 'social-media');
                  setSocialMediaFormData({
                    instagram: sm?.metadata?.instagram || '',
                    facebook: sm?.metadata?.facebook || '',
                    youtube: sm?.metadata?.youtube || '',
                    linkedin: sm?.metadata?.linkedin || '',
                    twitter: sm?.metadata?.twitter || ''
                  });
                  setEditingSocialMedia(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit
              </button>
            )}
          </div>

          {editingSocialMedia ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-instagram-line mr-2"></i>Instagram URL
                  </label>
                  <input
                    type="text"
                    value={socialMediaFormData.instagram}
                    onChange={(e) => setSocialMediaFormData({ ...socialMediaFormData, instagram: e.target.value })}
                    placeholder="https://instagram.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-facebook-fill mr-2"></i>Facebook URL
                  </label>
                  <input
                    type="text"
                    value={socialMediaFormData.facebook}
                    onChange={(e) => setSocialMediaFormData({ ...socialMediaFormData, facebook: e.target.value })}
                    placeholder="https://facebook.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-youtube-fill mr-2"></i>YouTube URL
                  </label>
                  <input
                    type="text"
                    value={socialMediaFormData.youtube}
                    onChange={(e) => setSocialMediaFormData({ ...socialMediaFormData, youtube: e.target.value })}
                    placeholder="https://youtube.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-linkedin-fill mr-2"></i>LinkedIn URL
                  </label>
                  <input
                    type="text"
                    value={socialMediaFormData.linkedin}
                    onChange={(e) => setSocialMediaFormData({ ...socialMediaFormData, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-twitter-fill mr-2"></i>Twitter URL
                  </label>
                  <input
                    type="text"
                    value={socialMediaFormData.twitter}
                    onChange={(e) => setSocialMediaFormData({ ...socialMediaFormData, twitter: e.target.value })}
                    placeholder="https://twitter.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => setEditingSocialMedia(false)}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-4">
              {socialMediaFormData.instagram && (
                <a href={socialMediaFormData.instagram} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              )}
              {socialMediaFormData.facebook && (
                <a href={socialMediaFormData.facebook} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
              )}
              {socialMediaFormData.youtube && (
                <a href={socialMediaFormData.youtube} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
              )}
              {socialMediaFormData.linkedin && (
                <a href={socialMediaFormData.linkedin} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              )}
              {socialMediaFormData.twitter && (
                <a href={socialMediaFormData.twitter} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-all duration-300">
                  <i className="ri-twitter-fill text-xl"></i>
                </a>
              )}
              {!socialMediaFormData.instagram && !socialMediaFormData.facebook && !socialMediaFormData.youtube && !socialMediaFormData.linkedin && !socialMediaFormData.twitter && (
                <p className="text-gray-500">No social media accounts added yet</p>
              )}
            </div>
          )}
        </div>
      );
    };

    const renderMapSectionModule = () => {
      const mapSection = getContentItem('contact', 'map-section');

      const handleSave = async () => {
        try {
          const item: ContentItem = {
            id: mapSection?.id || `temp-map-section-${Date.now()}`,
            page: 'contact',
            section: 'map-section',
            type: 'map',
            content: 'Map Section',
            order: 0,
            metadata: mapSectionFormData
          };
          await updateContentItem(item);
          setEditingMapSection(false);
          await reloadContent();
          alert('Map section saved successfully!');
        } catch (error) {
          alert('Failed to save!');
        }
      };

      return (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              Map Section
            </h3>
            {!editingMapSection && (
              <button
                onClick={() => {
                  const ms = getContentItem('contact', 'map-section');
                  setMapSectionFormData({
                    title: ms?.metadata?.title || 'Find Us',
                    highlight: ms?.metadata?.highlight || 'On The Map',
                    map_url: ms?.metadata?.map_url || ''
                  });
                  setEditingMapSection(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit
              </button>
            )}
          </div>

          {editingMapSection ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={mapSectionFormData.title}
                    onChange={(e) => setMapSectionFormData({ ...mapSectionFormData, title: e.target.value })}
                    placeholder="Find Us"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bold Title
                  </label>
                  <input
                    type="text"
                    value={mapSectionFormData.highlight}
                    onChange={(e) => setMapSectionFormData({ ...mapSectionFormData, highlight: e.target.value })}
                    placeholder="On The Map"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Google Maps Embed URL
                </label>
                <textarea
                  value={mapSectionFormData.map_url}
                  onChange={(e) => setMapSectionFormData({ ...mapSectionFormData, map_url: e.target.value })}
                  placeholder="https://www.google.com/maps/embed?pb=..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                />
                <p className="text-sm text-gray-500 mt-1">
                  💡 Use the "Embed a map" option on Google Maps to get the iframe URL
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => setEditingMapSection(false)}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="font-medium text-gray-900">
                  {mapSectionFormData.title} <span className="text-[#C5A46D]">{mapSectionFormData.highlight}</span>
                </p>
              </div>
              {mapSectionFormData.map_url && (
                <div className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden">
                  <iframe
                    src={mapSectionFormData.map_url}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              )}
              {!mapSectionFormData.map_url && (
                <p className="text-gray-500">No map added yet</p>
              )}
            </div>
          )}
        </div>
      );
    };

    const renderOfficeLocationsModule = () => {
      const officeLocations = content.filter(item => 
        item.page === 'contact' && 
        item.section === 'office-locations'
      ).sort((a, b) => (a.order || 0) - (b.order || 0));

      const handleAddOffice = () => {
        setEditingOffice({
          id: `temp-office-${Date.now()}`,
          page: 'contact',
          section: 'office-locations',
          type: 'office',
          content: '',
          order: officeLocations.length,
          metadata: { name: '', address: '', hours: '', phone: '', email: '', icon: 'ri-building-line' }
        });
        setOfficeFormData({ name: '', address: '', hours: '', phone: '', email: '', icon: 'ri-building-line' });
      };

      const handleSaveOffice = async () => {
        if (!officeFormData.name || !officeFormData.address) {
          alert('Please fill in at least the office name and address!');
          return;
        }

        try {
          const officeItem: ContentItem = {
            ...editingOffice!,
            content: officeFormData.name,
            metadata: officeFormData
          };
          await updateContentItem(officeItem);
          setEditingOffice(null);
          await reloadContent();
          alert('Office location saved successfully!');
        } catch (error) {
          alert('Failed to save!');
        }
      };

      const handleDeleteOffice = async (id: string) => {
        if (confirm('Are you sure you want to delete this office location?')) {
          try {
            await deleteContentItem(id);
            await reloadContent();
            alert('Ofis lokasyonu silindi!');
          } catch (error) {
            alert('Failed to delete!');
          }
        }
      };

      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Office Locations</h3>
            <button
              onClick={handleAddOffice}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
            >
              <i className="ri-add-line mr-2"></i>Yeni Ofis Ekle
            </button>
          </div>

          <div className="grid gap-4">
            {officeLocations.map((office) => (
              <div key={office.id} className="bg-white p-6 rounded-lg border shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 rounded-full border border-[#C5A46D] flex items-center justify-center flex-shrink-0">
                      <i className={`${office.metadata?.icon || 'ri-building-line'} text-[#C5A46D] text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-lg mb-2">{office.metadata?.name}</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        {office.metadata?.address && (
                          <p><i className="ri-map-pin-line mr-2"></i>{office.metadata.address}</p>
                        )}
                        {office.metadata?.hours && (
                          <p><i className="ri-time-line mr-2"></i>{office.metadata.hours}</p>
                        )}
                        {office.metadata?.phone && (
                          <p><i className="ri-phone-line mr-2"></i>{office.metadata.phone}</p>
                        )}
                        {office.metadata?.email && (
                          <p><i className="ri-mail-line mr-2"></i>{office.metadata.email}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingOffice(office);
                        setOfficeFormData({
                          name: office.metadata?.name || '',
                          address: office.metadata?.address || '',
                          hours: office.metadata?.hours || '',
                          phone: office.metadata?.phone || '',
                          email: office.metadata?.email || '',
                          icon: office.metadata?.icon || 'ri-building-line'
                        });
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm cursor-pointer whitespace-nowrap"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteOffice(office.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm cursor-pointer whitespace-nowrap"
                    >
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {editingOffice && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-xl font-semibold mb-4">
                  {editingOffice.id.includes('temp-') ? 'New Office Location' : 'Edit Office Location'}
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Office Name *</label>
                      <input
                        type="text"
                        value={officeFormData.name}
                        onChange={(e) => setOfficeFormData({ ...officeFormData, name: e.target.value })}
                        placeholder="e.g. Dubai Sales Office"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                      <select
                        value={officeFormData.icon}
                        onChange={(e) => setOfficeFormData({ ...officeFormData, icon: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white pr-8"
                      >
                        <option value="ri-building-line">Bina</option>
                        <option value="ri-home-line">Ev</option>
                        <option value="ri-customer-service-2-line">Customer Service</option>
                        <option value="ri-store-line">Store / Boutique</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Adres *</label>
                    <textarea
                      value={officeFormData.address}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, address: e.target.value })}
                      placeholder="e.g. Business Bay, Dubai, UAE"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Working Hours</label>
                    <input
                      type="text"
                      value={officeFormData.hours}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, hours: e.target.value })}
                      placeholder="e.g. Mon-Fri: 9:00 AM - 6:00 PM"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                      <input
                        type="text"
                        value={officeFormData.phone}
                        onChange={(e) => setOfficeFormData({ ...officeFormData, phone: e.target.value })}
                        placeholder="+971 4 123 4567"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={officeFormData.email}
                        onChange={(e) => setOfficeFormData({ ...officeFormData, email: e.target.value })}
                        placeholder="dubai@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSaveOffice}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer whitespace-nowrap"
                  >
                    Kaydet
                  </button>
                  <button
                    onClick={() => setEditingOffice(null)}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="grid grid-cols-6 gap-4">
        {/* Module Sidebar */}
        <div className="col-span-1 space-y-2">
          {contactModules.map((module) => (
            <button
              key={module.id}
              onClick={() => setActiveContactModule(module.id)}
              className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                activeContactModule === module.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className={`${module.icon} mr-2`}></i>
              <span className="text-sm">{module.name}</span>
            </button>
          ))}
        </div>

        {/* Module Content */}
        <div className="col-span-5">
          {activeContactModule === 'hero-section' && renderContactHeroSection()}
          {activeContactModule === 'left-section' && (() => {
            const leftTitleItem = getContentItem('contact', 'left-title');
            const leftSubtitleItem = getContentItem('contact', 'left-subtitle');
            const handleSaveLeft = async () => {
              try {
                const titleItem: ContentItem = {
                  id: leftTitleItem?.id || `temp-contact-left-title-${Date.now()}`,
                  page: 'contact',
                  section: 'left-title',
                  type: 'text',
                  content: leftTitleForm,
                  order: 0
                };
                const subtitleItem: ContentItem = {
                  id: leftSubtitleItem?.id || `temp-contact-left-subtitle-${Date.now()}`,
                  page: 'contact',
                  section: 'left-subtitle',
                  type: 'text',
                  content: leftSubtitleForm,
                  order: 0
                };
                await updateContentItem(titleItem);
                await updateContentItem(subtitleItem);
                setEditingLeftTexts(false);
                await reloadContent();
              } catch (error) {
                alert('Failed to save!');
              }
            };
            return (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <i className="ri-font-size mr-2"></i>
                      Left Title / Subtitle
                    </h3>
                    {!editingLeftTexts && (
                      <button
                        onClick={() => {
                          setEditingLeftTexts(true);
                          setLeftTitleForm(leftTitleItem?.content || '');
                          setLeftSubtitleForm(leftSubtitleItem?.content || '');
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {editingLeftTexts ? (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Left Title (Each line will appear separately)</label>
                        <textarea
                          value={leftTitleForm}
                          onChange={(e) => setLeftTitleForm(e.target.value)}
                          placeholder={"We’re Here to\nAssist You"}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Left Subtitle</label>
                        <textarea
                          value={leftSubtitleForm}
                          onChange={(e) => setLeftSubtitleForm(e.target.value)}
                          placeholder="Whether you’re seeking more information..."
                          rows={4}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button onClick={handleSaveLeft} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer">Save</button>
                        <button onClick={() => setEditingLeftTexts(false)} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="text-2xl font-light text-gray-900 whitespace-pre-line">{leftTitleItem?.content || '—'}</div>
                      <div className="text-gray-700">{leftSubtitleItem?.content || '—'}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          {activeContactModule === 'contact-info' && renderContactInfoModule()}
          {activeContactModule === 'quick-actions' && renderQuickActionsModule()}
          {activeContactModule === 'social-media' && renderSocialMediaModule()}
          {activeContactModule === 'map-section' && renderMapSectionModule()}
          {activeContactModule === 'office-locations' && renderOfficeLocationsModule()}
        </div>
      </div>
    );
  };

  // Contact Section save handlers
  const handleSaveContactTitle = async () => {
    if (!contactTitleForm.trim()) {
      alert('Please enter a title!');
      return;
    }

    try {
      const titleItem: ContentItem = {
        id: getContentItem('home', 'contact-title')?.id || `temp-contact-title-${Date.now()}`,
        page: 'home',
        section: 'contact-title',
        type: 'text',
        content: contactTitleForm,
        order: 0
      };

      await updateContentItem(titleItem);
      setContactTitleForm('');
      setEditingContactTitle(false);
      await reloadContent();
      alert('Title saved successfully!');
    } catch (error) {
      alert('Failed to save title!');
    }
  };

  const handleSaveContactForm = async () => {
    if (!contactFormDataForm.form_title.trim()) {
      alert('Please enter a form title!');
      return;
    }

    try {
      const formItem: ContentItem = {
        id: getContentItem('home', 'contact-form')?.id || `temp-contact-form-${Date.now()}`,
        page: 'home',
        section: 'contact-form',
        type: 'form_settings',
        content: 'Form Settings',
        order: 0,
        metadata: {
          form_title: contactFormDataForm.form_title,
          submit_button_text: contactFormDataForm.submit_button_text,
          default_country_code: contactFormDataForm.default_country_code
        }
      };

      await updateContentItem(formItem);
      setContactFormDataForm({ form_title: '', submit_button_text: '', default_country_code: '' });
      setEditingContactForm(false);
      await reloadContent();
      alert('Form settings saved successfully!');
    } catch (error) {
      alert('Failed to save form settings!');
    }
  };

  const handleSaveContactLinks = async () => {
    await saveContent('home', 'contact-links', 'Contact Links', contactLinksData, 'settings');
    setEditingContactLinks(false);
  };

  const handleSaveContactSocial = async () => {
    await saveContent('home', 'contact-social', 'Social Media', contactSocialData, 'settings');
    setEditingContactSocial(false);
  };

  const handleSaveContactIcons = async () => {
    await saveContent('home', 'contact-icons', 'Contact Icons', contactIconsData, 'settings');
    setEditingContactIcons(false);
  };

  const renderResidencesManager = () => {
    const unitTabs = [
      { id: 'studio', label: 'Studio' },
      { id: '1bedroom', label: '1 Bedroom' },
      { id: '2bedroom', label: '2 Bedroom' },
      { id: 'penthouse', label: 'Penthouse' }
    ] as const;

    const heroItem = getContentItem('residences', `${activeResidencesUnit}-hero`);
    const currentHeroImage = heroItem?.image_url || heroItem?.metadata?.image_url || '';
    const currentHeroVideo = heroItem?.metadata?.video_url || heroItem?.video_url || '';
    const currentHeroPoster = heroItem?.metadata?.poster_url || '';

    // Location image per unit
    const locationItem = getContentItem('residences', `${activeResidencesUnit}-location`);
    const currentLocationImage = locationItem?.image_url || locationItem?.metadata?.image_url || '';

    // Gallery per unit
    const galleryItem = getContentItem('residences', `${activeResidencesUnit}-gallery`);

    // Floor Plan (gallery) per unit
    const floorplanSingleItem = getContentItem('residences', `${activeResidencesUnit}-floorplan`);
    const floorplanImages = (Array.isArray(floorplanSingleItem?.metadata?.images) ? floorplanSingleItem!.metadata.images : [])
      .map((it: any) => (typeof it === 'string' ? { image: it } : { image: it?.image || it?.url || it?.src || '' }))
      .filter((x: any) => x.image);

    // Typical Floorplan (gallery) per unit
    const typicalFloorplanItem = getContentItem('residences', `${activeResidencesUnit}-typical-floorplan`);
    const typicalImages = (Array.isArray(typicalFloorplanItem?.metadata?.images) ? typicalFloorplanItem!.metadata.images : [])
      .map((it: any) => (typeof it === 'string' ? { image: it } : { image: it?.image || it?.url || it?.src || '' }))
      .filter((x: any) => x.image);

    // 3D View (gallery) per unit
    const view3DItem = getContentItem('residences', `${activeResidencesUnit}-3dview`);

    // Footer (page-wide for Residences)
    const footerItem = getContentItem('residences', `footer`);
    const virtualTourItem = getContentItem('residences', 'virtual-tour');
    const footerDefaults = {
      bg_image_url: footerItem?.image_url || footerItem?.metadata?.bg_image_url || '',
      left_title_line1: footerItem?.metadata?.left_title_line1 || 'Connect with',
      left_title_bold: footerItem?.metadata?.left_title_bold || 'the brand',
      left_link1_text: footerItem?.metadata?.left_link1_text || 'FIND A SALES BOUTIQUE',
      left_link1_url: footerItem?.metadata?.left_link1_url || '#',
      left_link2_text: footerItem?.metadata?.left_link2_text || 'DISCOVER THE COLLECTION',
      left_link2_url: footerItem?.metadata?.left_link2_url || '#',
      form_title_line1: footerItem?.metadata?.form_title_line1 || 'Express',
      form_title_bold: footerItem?.metadata?.form_title_bold || 'Your Interest',
      submit_button_text: footerItem?.metadata?.submit_button_text || 'MEET OUR BRAND AMBASSADOR',
      terms_text: footerItem?.metadata?.terms_text || 'By submitting, you agree to our terms & conditions*'
    };

    // Residences Page-wide Hero Text
    const residencesHeroTextItem = getContentItem('residences', 'hero-text');
    const residencesHeroDefaults = {
      title_line1: residencesHeroTextItem?.metadata?.title_line1 || 'Residences',
      title_bold: residencesHeroTextItem?.metadata?.title_bold || '',
      subtitle: residencesHeroTextItem?.metadata?.subtitle || '',
      button_text: residencesHeroTextItem?.metadata?.button_text || '',
      button_url: residencesHeroTextItem?.metadata?.button_url || ''
    } as any;

    const upsertResidencesHeroText = async (data: { title_line1?: string; title_bold?: string; subtitle?: string; button_text?: string; button_url?: string }) => {
      const payload: ContentItem = {
        id: residencesHeroTextItem?.id || `temp-residences-hero-text-${Date.now()}`,
        page: 'residences',
        section: 'hero-text',
        type: 'text',
        content: 'Residences Hero Text',
        order: 0,
        metadata: {
          ...(residencesHeroTextItem?.metadata || {}),
          title_line1: data.title_line1 ?? residencesHeroDefaults.title_line1,
          title_bold: data.title_bold ?? residencesHeroDefaults.title_bold,
          subtitle: data.subtitle ?? residencesHeroDefaults.subtitle,
          button_text: data.button_text ?? residencesHeroDefaults.button_text,
          button_url: data.button_url ?? residencesHeroDefaults.button_url
        }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
      alert('Residences Hero text saved successfully!');
    };

    // Intro (WHY) content per unit
    const introItem = getContentItem('residences', `${activeResidencesUnit}-intro`);
    const introDefaults = {
      title: introItem?.metadata?.title || 'WHY',
      highlight: introItem?.metadata?.highlight || 'GOLDEN TOWER?',
      description: introItem?.metadata?.description || '',
      button_text: introItem?.metadata?.button_text || 'REGISTER YOUR INTEREST',
      button_url: introItem?.metadata?.button_url || ''
    };

    const upsertIntro = async (data: { title: string; highlight: string; description: string; button_text: string; button_url: string; }) => {
      const payload: ContentItem = {
        id: introItem?.id || `temp-residences-${activeResidencesUnit}-intro-${Date.now()}`,
        page: 'residences',
        section: `${activeResidencesUnit}-intro`,
        type: 'intro',
        content: 'Residences Intro',
        order: 0,
        metadata: {
          ...(introItem?.metadata || {}),
          title: data.title,
          highlight: data.highlight,
          description: data.description,
          button_text: data.button_text,
          button_url: data.button_url
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Intro section saved successfully!');
    };

    const upsertHero = async (partial: { image_url?: string; video_url?: string; poster_url?: string }) => {
      const payload: ContentItem = {
        id: heroItem?.id || `temp-residences-${activeResidencesUnit}-hero-${Date.now()}`,
        page: 'residences',
        section: `${activeResidencesUnit}-hero`,
        type: 'hero',
        content: 'Residences Hero',
        order: 0,
        image_url: partial.image_url ?? (heroItem?.image_url || heroItem?.metadata?.image_url || ''),
        video_url: partial.video_url ?? (heroItem?.video_url || heroItem?.metadata?.video_url || ''),
        metadata: {
          ...(heroItem?.metadata || {}),
          image_url: partial.image_url ?? (heroItem?.metadata?.image_url || heroItem?.image_url || ''),
          video_url: partial.video_url ?? (heroItem?.metadata?.video_url || heroItem?.video_url || ''),
          poster_url: partial.poster_url ?? (heroItem?.metadata?.poster_url || '')
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Hero kaydedildi');
    };

    const upsertLocation = async (partial: { image_url?: string }) => {
      const payload: ContentItem = {
        id: locationItem?.id || `temp-residences-${activeResidencesUnit}-location-${Date.now()}`,
        page: 'residences',
        section: `${activeResidencesUnit}-location`,
        type: 'location',
        content: 'Residences Location',
        order: 0,
        image_url: partial.image_url ?? (locationItem?.image_url || locationItem?.metadata?.image_url || ''),
        metadata: {
          ...(locationItem?.metadata || {}),
          image_url: partial.image_url ?? (locationItem?.metadata?.image_url || locationItem?.image_url || '')
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Location image saved successfully!');
    };

    const upsertFloorplanGallery = async (images: { image: string }[]) => {
      const payload: ContentItem = {
        id: floorplanSingleItem?.id || `temp-residences-${activeResidencesUnit}-floorplan-${Date.now()}`,
        page: 'residences',
        section: `${activeResidencesUnit}-floorplan`,
        type: 'gallery',
        content: 'Residences Floor Plan',
        order: 0,
        metadata: {
          ...(floorplanSingleItem?.metadata || {}),
          images
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Floor Plan images saved successfully!');
    };

    const upsertTypicalGallery = async (images: { image: string }[]) => {
      const payload: ContentItem = {
        id: typicalFloorplanItem?.id || `temp-residences-${activeResidencesUnit}-typical-floorplan-${Date.now()}`,
        page: 'residences',
        section: `${activeResidencesUnit}-typical-floorplan`,
        type: 'gallery',
        content: 'Residences Typical Floorplan',
        order: 0,
        metadata: {
          ...(typicalFloorplanItem?.metadata || {}),
          images
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Typical Floorplan images saved successfully!');
    };

    const upsertGallery = async (images: { image: string; category?: string }[]) => {
      const payload: ContentItem = {
        id: galleryItem?.id || `temp-residences-${activeResidencesUnit}-gallery-${Date.now()}`,
        page: 'residences',
        section: `${activeResidencesUnit}-gallery`,
        type: 'gallery',
        content: 'Residences Gallery',
        order: 0,
        metadata: {
          ...(galleryItem?.metadata || {}),
          images
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Gallery kaydedildi');
    };

    const upsert3DView = async (images: { image: string; category?: string }[]) => {
      const payload: ContentItem = {
        id: view3DItem?.id || `temp-residences-${activeResidencesUnit}-3dview-${Date.now()}`,
        page: 'residences',
        section: `${activeResidencesUnit}-3dview`,
        type: 'gallery',
        content: 'Residences 3D View',
        order: 0,
        metadata: {
          ...(view3DItem?.metadata || {}),
          images
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('3D View images saved successfully!');
    };

    const upsertVirtualTour = async (show: boolean) => {
      const payload: ContentItem = {
        id: virtualTourItem?.id || `temp-residences-virtual-tour-${Date.now()}`,
        page: 'residences',
        section: 'virtual-tour',
        type: 'settings',
        content: 'Virtual Tour Settings',
        order: 0,
        metadata: {
          ...(virtualTourItem?.metadata || {}),
          show
        }
      };
      await updateContentItem(payload);
      await reloadContent();
    };

    const upsertFooter = async (data: {
      bg_image_url?: string;
      left_title_line1?: string;
      left_title_bold?: string;
      left_link1_text?: string;
      left_link1_url?: string;
      left_link2_text?: string;
      left_link2_url?: string;
      form_title_line1?: string;
      form_title_bold?: string;
      submit_button_text?: string;
      terms_text?: string;
    }) => {
      const payload: ContentItem = {
        id: footerItem?.id || `temp-residences-footer-${Date.now()}`,
        page: 'residences',
        section: 'footer',
        type: 'footer',
        content: 'Residences Footer',
        order: 0,
        image_url: data.bg_image_url ?? (footerItem?.image_url || footerItem?.metadata?.bg_image_url || ''),
        metadata: {
          ...(footerItem?.metadata || {}),
          bg_image_url: data.bg_image_url ?? (footerItem?.metadata?.bg_image_url || footerItem?.image_url || ''),
          left_title_line1: data.left_title_line1 ?? footerDefaults.left_title_line1,
          left_title_bold: data.left_title_bold ?? footerDefaults.left_title_bold,
          left_link1_text: data.left_link1_text ?? footerDefaults.left_link1_text,
          left_link1_url: data.left_link1_url ?? footerDefaults.left_link1_url,
          left_link2_text: data.left_link2_text ?? footerDefaults.left_link2_text,
          left_link2_url: data.left_link2_url ?? footerDefaults.left_link2_url,
          form_title_line1: data.form_title_line1 ?? footerDefaults.form_title_line1,
          form_title_bold: data.form_title_bold ?? footerDefaults.form_title_bold,
          submit_button_text: data.submit_button_text ?? footerDefaults.submit_button_text,
          terms_text: data.terms_text ?? footerDefaults.terms_text
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Footer saved successfully!');
    };

    const renderSectionEditor = (sectionKey: 'section1' | 'section2' | 'section3' | 'section4') => {
      const item = getContentItem('residences', `${activeResidencesUnit}-${sectionKey}`);
      const raw = Array.isArray(item?.metadata?.images) ? item!.metadata.images : [];
      const images: { image: string; text: string }[] = raw
        .map((it: any) => {
          if (typeof it === 'string') return { image: it, text: '' };
          const image = it?.image || it?.url || it?.src || '';
          const text = it?.text || it?.caption || '';
          return image ? { image, text } : null;
        })
        .filter(Boolean) as { image: string; text: string }[];

      const upsertSection = async (next: { addImage?: string; removeIndex?: number; updateIndex?: number; updateText?: string }) => {
        const nextImages = [...images];
        if (typeof next.removeIndex === 'number') {
          nextImages.splice(next.removeIndex, 1);
        }
        if (typeof next.updateIndex === 'number') {
          const idx = next.updateIndex;
          if (nextImages[idx]) nextImages[idx] = { ...nextImages[idx], text: next.updateText || '' };
        }
        if (next.addImage) nextImages.push({ image: next.addImage, text: '' });

        const payload: ContentItem = {
          id: item?.id || `temp-residences-${activeResidencesUnit}-${sectionKey}-${Date.now()}`,
          page: 'residences',
          section: `${activeResidencesUnit}-${sectionKey}`,
          type: 'section',
          content: 'Residences Section',
          order: 0,
          metadata: {
            ...(item?.metadata || {}),
            images: nextImages
          }
        };
        await updateContentItem(payload);
        await reloadContent();
        alert('Section kaydedildi');
      };

      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg p-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Images</h4>
            <MediaUploader
              type="image"
              label="Add New Image"
              onUploadComplete={(url) => upsertSection({ addImage: url })}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {images.length > 0 ? (
                images.map((img, idx) => (
                  <div key={idx} className="bg-gray-50 rounded border p-3">
                    <div className="flex gap-3">
                      <img src={img.image} alt={`Section image ${idx + 1}`} className="w-40 h-28 object-cover rounded border" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image Caption</label>
                        <textarea
                          id={`${sectionKey}-caption-${idx}`}
                          defaultValue={img.text}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => {
                              const val = (document.getElementById(`${sectionKey}-caption-${idx}`) as HTMLTextAreaElement)?.value || '';
                              upsertSection({ updateIndex: idx, updateText: val });
                            }}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          >
                            Update
                          </button>
                          <button
                            onClick={() => upsertSection({ removeIndex: idx })}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                          >
                            Sil
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-sm">No images added yet</div>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        {/* Unit Tabs + Shared Footer Tab */}
        <div className="flex gap-2 border-b">
          {unitTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => { setActiveResidencesUnit(t.id); setIsResidencesFooterMode(false); }}
              className={`px-4 py-2 font-medium transition-colors ${
                !isResidencesFooterMode && activeResidencesUnit === t.id ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={() => setIsResidencesFooterMode(true)}
            className={`px-4 py-2 font-medium transition-colors ${
              isResidencesFooterMode ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Footer
          </button>
        </div>

        <div className="grid grid-cols-6 gap-4">
          {isResidencesFooterMode ? (
            <div className="col-span-6">
              {/* Footer Editor (shared) */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Footer Content</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <MediaUploader
                        type="image"
                        currentUrl={footerDefaults.bg_image_url}
                        label="Background Image"
                        onUploadComplete={(url) => upsertFooter({ bg_image_url: url })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Left Title (line 1)</label>
                      <input id="footer-left-title-line1" type="text" defaultValue={footerDefaults.left_title_line1} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Left Title (bold)</label>
                      <input id="footer-left-title-bold" type="text" defaultValue={footerDefaults.left_title_bold} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Left Link 1 Text</label>
                      <input id="footer-left-link1-text" type="text" defaultValue={footerDefaults.left_link1_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Left Link 1 URL</label>
                      <input id="footer-left-link1-url" type="text" defaultValue={footerDefaults.left_link1_url} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Left Link 2 Text</label>
                      <input id="footer-left-link2-text" type="text" defaultValue={footerDefaults.left_link2_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Left Link 2 URL</label>
                      <input id="footer-left-link2-url" type="text" defaultValue={footerDefaults.left_link2_url} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Form Title (line 1)</label>
                      <input id="footer-form-title-line1" type="text" defaultValue={footerDefaults.form_title_line1} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Form Title (bold)</label>
                      <input id="footer-form-title-bold" type="text" defaultValue={footerDefaults.form_title_bold} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Form Button Text</label>
                      <input id="footer-submit-button-text" type="text" defaultValue={footerDefaults.submit_button_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions Text</label>
                      <input id="footer-terms-text" type="text" defaultValue={footerDefaults.terms_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => {
                        const data = {
                          left_title_line1: (document.getElementById('footer-left-title-line1') as HTMLInputElement)?.value || '',
                          left_title_bold: (document.getElementById('footer-left-title-bold') as HTMLInputElement)?.value || '',
                          left_link1_text: (document.getElementById('footer-left-link1-text') as HTMLInputElement)?.value || '',
                          left_link1_url: (document.getElementById('footer-left-link1-url') as HTMLInputElement)?.value || '',
                          left_link2_text: (document.getElementById('footer-left-link2-text') as HTMLInputElement)?.value || '',
                          left_link2_url: (document.getElementById('footer-left-link2-url') as HTMLInputElement)?.value || '',
                          form_title_line1: (document.getElementById('footer-form-title-line1') as HTMLInputElement)?.value || '',
                          form_title_bold: (document.getElementById('footer-form-title-bold') as HTMLInputElement)?.value || '',
                          submit_button_text: (document.getElementById('footer-submit-button-text') as HTMLInputElement)?.value || '',
                          terms_text: (document.getElementById('footer-terms-text') as HTMLInputElement)?.value || ''
                        };
                        upsertFooter(data);
                      }}
                      className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Kaydet
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Module Sidebar */}
              <div className="col-span-1 space-y-2">
                {[
                  { id: 'hero', name: 'Hero', icon: 'ri-layout-top-line' },
                  { id: 'intro', name: 'Intro (WHY)', icon: 'ri-question-line' },
                  { id: 'gallery', name: 'Gallery', icon: 'ri-image-add-line' },
                  { id: '3dview', name: '3D View', icon: 'ri-cube-3-line' },
                  { id: 'floorplan', name: 'Floor Plan', icon: 'ri-layout-2-line' },
                  { id: 'typical-floorplan', name: 'Typical Floorplan', icon: 'ri-layout-2-line' },
                  { id: 'section1', name: 'Section 1', icon: 'ri-image-2-line' },
                  { id: 'section2', name: 'Section 2', icon: 'ri-image-2-line' },
                  { id: 'section3', name: 'Section 3', icon: 'ri-image-2-line' },
                  { id: 'section4', name: 'Section 4', icon: 'ri-image-2-line' },
                  { id: 'virtual-tour', name: '360 Virtual Tour', icon: 'ri-360-line' }
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setActiveResidencesModule(m.id)}
                    className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                      activeResidencesModule === m.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${m.icon} mr-2`}></i>
                    <span className="text-sm">{m.name}</span>
                  </button>
                ))}
              </div>

              {/* Module Content */}
              <div className="col-span-5">
            {activeResidencesModule === 'hero' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Hero Media</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <MediaUploader
                      type="video"
                      currentUrl={currentHeroVideo}
                      label="Hero Video"
                      onUploadComplete={(url) => upsertHero({ video_url: url })}
                    />
                    <MediaUploader
                      type="image"
                      currentUrl={currentHeroPoster}
                      label="Poster Image (for video)"
                      onUploadComplete={(url) => upsertHero({ poster_url: url })}
                    />
                  </div>
                  <div className="mt-6">
                    <MediaUploader
                      type="image"
                      currentUrl={currentHeroImage}
                      label="Hero Image (used if no video)"
                      onUploadComplete={(url) => upsertHero({ image_url: url })}
                    />
                  </div>
                  {currentHeroVideo && (
                    <div className="mt-4">
                      <button
                        onClick={() => upsertHero({ video_url: '' })}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Remove Video
                      </button>
                    </div>
                  )}
                </div>
                {showResidencesGalleryBulk && (
                  <BulkImageUploader
                    onUploadComplete={(url) => {
                      const raw = Array.isArray(galleryItem?.metadata?.images) ? galleryItem!.metadata.images : [];
                      const normalized = raw.map((it: any) => (
                        typeof it === 'string' ? { image: it, category: '' } : { image: it?.image || it?.url || it?.src || '', category: it?.category || '' }
                      )).filter((x: any) => x.image);
                      normalized.push({ image: url, category: '' });
                      upsertGallery(normalized);
                    }}
                    onClose={() => setShowResidencesGalleryBulk(false)}
                  />
                )}
              </div>
            )}

            {activeResidencesModule === 'intro' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Intro (WHY) Content</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input id="intro-title" type="text" defaultValue={introDefaults.title} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Accent / Bold Title</label>
                      <input id="intro-highlight" type="text" defaultValue={introDefaults.highlight} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea id="intro-description" rows={4} defaultValue={introDefaults.description} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                      <input id="intro-button-text" type="text" defaultValue={introDefaults.button_text} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Buton URL</label>
                      <input id="intro-button-url" type="text" defaultValue={introDefaults.button_url} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => {
                        const title = (document.getElementById('intro-title') as HTMLInputElement)?.value || '';
                        const highlight = (document.getElementById('intro-highlight') as HTMLInputElement)?.value || '';
                        const description = (document.getElementById('intro-description') as HTMLTextAreaElement)?.value || '';
                        const button_text = (document.getElementById('intro-button-text') as HTMLInputElement)?.value || '';
                        const button_url = (document.getElementById('intro-button-url') as HTMLInputElement)?.value || '';
                        upsertIntro({ title, highlight, description, button_text, button_url });
                      }}
                      className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Kaydet
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeResidencesModule === 'location' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Location Image</h3>
                  <MediaUploader
                    type="image"
                    currentUrl={currentLocationImage}
                    label="LOCATION Image (Full width)"
                    onUploadComplete={(url) => upsertLocation({ image_url: url })}
                  />
                </div>
              </div>
            )}

            {activeResidencesModule === 'gallery' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Gallery Images</h3>
                  <div className="flex gap-3 mb-4">
                    <button
                      onClick={() => setShowResidencesGalleryBulk(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Bulk Upload
                    </button>
                  </div>
                  <MediaUploader
                    type="image"
                    label="Add New Image"
                    onUploadComplete={(url) => {
                      const raw = Array.isArray(galleryItem?.metadata?.images) ? galleryItem!.metadata.images : [];
                      const normalized = raw.map((it: any) => (
                        typeof it === 'string' ? { image: it, category: '' } : { image: it?.image || it?.url || it?.src || '', category: it?.category || '' }
                      )).filter((x: any) => x.image);
                      normalized.push({ image: url, category: '' });
                      upsertGallery(normalized);
                    }}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {(Array.isArray(galleryItem?.metadata?.images) ? galleryItem!.metadata.images : []).length > 0 ? (
                      (Array.isArray(galleryItem?.metadata?.images) ? galleryItem!.metadata.images : []).map((it: any, idx: number) => {
                        const img = typeof it === 'string' ? { image: it, category: '' } : { image: it?.image || it?.url || it?.src || '', category: it?.category || '' };
                        return (
                          <div key={idx} className="bg-gray-50 rounded border p-3">
                            <div className="flex gap-3">
                              <img src={img.image} alt={`Gallery ${idx + 1}`} className="w-40 h-28 object-cover rounded border" />
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                                <input
                                  id={`gallery-cat-${idx}`}
                                  type="text"
                                  defaultValue={img.category}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                                />
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() => {
                                      const val = (document.getElementById(`gallery-cat-${idx}`) as HTMLInputElement)?.value || '';
                                      const raw = Array.isArray(galleryItem?.metadata?.images) ? galleryItem!.metadata.images : [];
                                      const normalized = raw.map((item: any, i: number) => {
                                        const base = typeof item === 'string' ? { image: item, category: '' } : { image: item?.image || item?.url || item?.src || '', category: item?.category || '' };
                                        return i === idx ? { ...base, category: val } : base;
                                      }).filter((x: any) => x.image);
                                      upsertGallery(normalized);
                                    }}
                                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                  >
                                    Update
                                  </button>
                                  <button
                                    onClick={() => {
                                      const raw = Array.isArray(galleryItem?.metadata?.images) ? galleryItem!.metadata.images : [];
                                      const normalized = raw.map((item: any) => (
                                        typeof item === 'string' ? { image: item, category: '' } : { image: item?.image || item?.url || item?.src || '', category: item?.category || '' }
                                      )).filter((x: any) => x.image);
                                      normalized.splice(idx, 1);
                                      upsertGallery(normalized);
                                    }}
                                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                                  >
                                    Sil
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-gray-500 text-sm">No images added yet</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeResidencesModule === 'floorplan' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Floor Plan Images</h3>
                  <MediaUploader
                    type="image"
                    label="Add New Image"
                    onUploadComplete={(url) => {
                      const next = [...floorplanImages, { image: url }];
                      upsertFloorplanGallery(next);
                    }}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {floorplanImages.length > 0 ? (
                      floorplanImages.map((img, idx) => (
                        <div key={idx} className="bg-gray-50 rounded border p-3">
                          <div className="flex gap-3 items-start">
                            <img src={img.image} alt={`Floorplan ${idx + 1}`} className="w-40 h-28 object-cover rounded border" />
                            <div className="flex-1">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    const next = [...floorplanImages];
                                    next.splice(idx, 1);
                                    upsertFloorplanGallery(next);
                                  }}
                                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                                >
                                  Sil
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500 text-sm">No images added yet</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeResidencesModule === 'typical-floorplan' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Typical Floorplan Images</h3>
                  <MediaUploader
                    type="image"
                    label="Add New Image"
                    onUploadComplete={(url) => {
                      const next = [...typicalImages, { image: url }];
                      upsertTypicalGallery(next);
                    }}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {typicalImages.length > 0 ? (
                      typicalImages.map((img, idx) => (
                        <div key={idx} className="bg-gray-50 rounded border p-3">
                          <div className="flex gap-3 items-start">
                            <img src={img.image} alt={`Typical ${idx + 1}`} className="w-40 h-28 object-cover rounded border" />
                            <div className="flex-1">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    const next = [...typicalImages];
                                    next.splice(idx, 1);
                                    upsertTypicalGallery(next);
                                  }}
                                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                                >
                                  Sil
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500 text-sm">No images added yet</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeResidencesModule === '3dview' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">3D View Images</h3>
                  <div className="flex gap-3 mb-4">
                    <button
                      onClick={() => setShowResidences3DBulk(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Bulk Upload
                    </button>
                  </div>
                  <MediaUploader
                    type="image"
                    label="Add New Image"
                    onUploadComplete={(url) => {
                      const raw = Array.isArray(view3DItem?.metadata?.images) ? view3DItem!.metadata.images : [];
                      const normalized = raw.map((it: any) => (
                        typeof it === 'string' ? { image: it, category: '' } : { image: it?.image || it?.url || it?.src || '', category: it?.category || '' }
                      )).filter((x: any) => x.image);
                      normalized.push({ image: url, category: '' });
                      upsert3DView(normalized);
                    }}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {(Array.isArray(view3DItem?.metadata?.images) ? view3DItem!.metadata.images : []).length > 0 ? (
                      (Array.isArray(view3DItem?.metadata?.images) ? view3DItem!.metadata.images : []).map((it: any, idx: number) => {
                        const img = typeof it === 'string' ? { image: it, category: '' } : { image: it?.image || it?.url || it?.src || '', category: it?.category || '' };
                        return (
                          <div key={idx} className="bg-gray-50 rounded border p-3">
                            <div className="flex gap-3">
                              <img src={img.image} alt={`3D View ${idx + 1}`} className="w-40 h-28 object-cover rounded border" />
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                                <input
                                  id={`view3d-cat-${idx}`}
                                  type="text"
                                  defaultValue={img.category}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                                />
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() => {
                                      const val = (document.getElementById(`view3d-cat-${idx}`) as HTMLInputElement)?.value || '';
                                      const raw = Array.isArray(view3DItem?.metadata?.images) ? view3DItem!.metadata.images : [];
                                      const normalized = raw.map((item: any, i: number) => {
                                        const base = typeof item === 'string' ? { image: item, category: '' } : { image: item?.image || item?.url || item?.src || '', category: item?.category || '' };
                                        return i === idx ? { ...base, category: val } : base;
                                      }).filter((x: any) => x.image);
                                      upsert3DView(normalized);
                                    }}
                                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                  >
                                    Update
                                  </button>
                                  <button
                                    onClick={() => {
                                      const raw = Array.isArray(view3DItem?.metadata?.images) ? view3DItem!.metadata.images : [];
                                      const normalized = raw.map((item: any) => (
                                        typeof item === 'string' ? { image: item, category: '' } : { image: item?.image || item?.url || item?.src || '', category: item?.category || '' }
                                      )).filter((x: any) => x.image);
                                      normalized.splice(idx, 1);
                                      upsert3DView(normalized);
                                    }}
                                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                                  >
                                    Sil
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-gray-500 text-sm">No images added yet</div>
                    )}
                  </div>
                </div>
                {showResidences3DBulk && (
                  <BulkImageUploader
                    onUploadComplete={(url) => {
                      const raw = Array.isArray(view3DItem?.metadata?.images) ? view3DItem!.metadata.images : [];
                      const normalized = raw.map((it: any) => (
                        typeof it === 'string' ? { image: it, category: '' } : { image: it?.image || it?.url || it?.src || '', category: it?.category || '' }
                      )).filter((x: any) => x.image);
                      normalized.push({ image: url, category: '' });
                      upsert3DView(normalized);
                    }}
                    onClose={() => setShowResidences3DBulk(false)}
                  />
                )}
              </div>
            )}

            {activeResidencesModule === 'virtual-tour' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">360 Virtual Tour Settings</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Show Virtual Tour</label>
                        <p className="text-sm text-gray-500">Show/hide this section on the page</p>
                      </div>
                      <button
                        onClick={() => upsertVirtualTour(!virtualTourItem?.metadata?.show)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          virtualTourItem?.metadata?.show !== false ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            virtualTourItem?.metadata?.show !== false ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Virtual Tour URL: <code className="bg-gray-200 px-2 py-1 rounded text-xs">https://kuula.co/share/collection/71RXS</code>
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Note: To change the URL, update it in the code file.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeResidencesModule === 'section1' && renderSectionEditor('section1')}
            {activeResidencesModule === 'section2' && renderSectionEditor('section2')}
            {activeResidencesModule === 'section3' && renderSectionEditor('section3')}
            {activeResidencesModule === 'section4' && renderSectionEditor('section4')}
          </div>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Content Management</h2>
        <button
          onClick={reloadContent}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          <i className="ri-refresh-line mr-2"></i>Refresh
        </button>
      </div>

      {/* Page Tabs */}
      <div className="flex gap-2 border-b">
        {pages.map((page) => (
          <button
            key={page.id}
            onClick={() => {
              setActiveTab(page.id);
              setActiveModule('hero');
            }}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === page.id
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {page.name}
          </button>
        ))}
      </div>

      {/* Home Page Modules */}
      {activeTab === 'home' && (
        <div className="grid grid-cols-6 gap-4">
          {/* Module Sidebar */}
          <div className="col-span-1 space-y-2">
            {homeModules.map((module) => (
              <button
                key={module.id}
                onClick={() => setActiveModule(module.id)}
                className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                  activeModule === module.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={`${module.icon} mr-2`}></i>
                <span className="text-sm">{module.name}</span>
              </button>
            ))}
          </div>

          {/* Module Content */}
          <div className="col-span-5">
            {activeModule === 'hero' && renderHeroVideoModule()}
            {activeModule === 'hero-text' && renderHomeHeroTextModule()}
            {activeModule === 'home-intro' && renderHomeIntroModule()}
            {activeModule === 'projects-slider' && renderProjectsSlider()}
            {activeModule === 'luxury-collection' && renderLuxuryCollectionModule()}
            {activeModule === 'home-text1' && renderHomeTextBlock1Module()}
            {activeModule === 'pinnacle-slider' && renderPinnacleSliderModule()}
            {activeModule === 'awards' && renderAwardsManager()}
            {activeModule === 'awards-gallery' && renderHomeAwardsGalleryModule()}
            {activeModule === 'contact' && renderContactSectionModuleFor('home')}
          </div>
        </div>
      )}

      {/* Contact Page */}
      {activeTab === 'contact' && renderContactPageManager()}

      {/* Amenities Page */}
      {activeTab === 'amenities' && renderAmenitiesManager()}

      {/* Brand Page */}
      {activeTab === 'brand' && (
        <BrandContentManager />
      )}

      {/* Residences Page */}
      {activeTab === 'new-releases' && (
        <>
          {renderResidencesManager()}
          <div className="mt-10">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Residences Contact Section</h3>
            {renderContactSectionModuleFor('residences')}
          </div>
        </>
      )}
    </div>
  );
}
